import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Paper, Typography, IconButton, Tooltip, Chip, Box, Popover, Grid, Icon } from '@material-ui/core';
import { ZoomIn, ZoomOut, CenterFocusStrong, Refresh, ViewModule, ViewList, Search } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import * as ClusterActions from '../../store/actions';

const useStyles = makeStyles(theme => ({
	container: {
		position: 'relative',
		minHeight: 600, // Minimum height instead of fixed
		backgroundColor: '#fafafa', // Reverted to original
		border: '1px solid #e0e0e0' // Reverted to original
	},
	controls: {
		position: 'absolute',
		top: 10,
		right: 10,
		zIndex: 100,
		display: 'flex',
		flexDirection: 'column',
		gap: 5,
		width: 50 // Ensure consistent width for calculations
	},
	controlButton: {
		backgroundColor: 'rgba(255, 255, 255, 0.9)',
		'&:hover': {
			backgroundColor: 'rgba(255, 255, 255, 1)'
		}
	},
	header: {
		position: 'absolute',
		top: 10,
		left: 10,
		zIndex: 200,
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		backgroundColor: 'rgba(255, 255, 255, 0.98)',
		padding: '8px 12px',
		borderRadius: 4,
		border: '1px solid #e0e0e0',
		boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
	},
	stats: {
		position: 'absolute',
		bottom: 10,
		right: 10,
		backgroundColor: 'rgba(255, 255, 255, 0.95)',
		padding: 10,
		borderRadius: 4,
		fontSize: '12px',
		zIndex: 100,
		minWidth: 200
	},
	legend: {
		position: 'absolute',
		bottom: 10,
		left: 10,
		backgroundColor: 'rgba(255, 255, 255, 0.95)',
		padding: 10,
		borderRadius: 4,
		fontSize: '12px',
		zIndex: 100
	},
	rackDetails: {
		position: 'absolute',
		top: 60,
		left: 10,
		backgroundColor: 'rgba(255, 255, 255, 0.98)',
		padding: 12,
		borderRadius: 4,
		fontSize: '12px',
		zIndex: 200,
		minWidth: 250,
		maxHeight: 400,
		overflowY: 'auto',
		border: '1px solid #ccc',
		boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
	}
}));

const TopologyMapScalable = ({ nodes = [], cluster, healthData }) => {
	const svgRef = useRef();
	const containerRef = useRef();
	const classes = useStyles();
	const dispatch = useDispatch();
	const [viewMode, setViewMode] = useState('hierarchical'); // hierarchical, grid, force
	const [selectedRack, setSelectedRack] = useState(null);
	const [zoomLevel, setZoomLevel] = useState(1);
	const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
	const [selectedGpuData, setSelectedGpuData] = useState(null);
	const [selectedNodeData, setSelectedNodeData] = useState(null);
	const [tooltip, setTooltip] = useState(null);
	const [dynamicHeight, setDynamicHeight] = useState(600); // Start with smaller height
	
	// Persistent zoom/pan state across updates
	const currentTransformRef = useRef(d3.zoomIdentity);
	const isUserInteractingRef = useRef(false);
	const updateTimeoutRef = useRef(null);
	const lastInteractionTimeRef = useRef(0);
	
	// Use refs for timeouts to prevent stale closures
	const popoverTimeout = useRef(null);
	const hoverTimeout = useRef(null);
	const tooltipTimeout = useRef(null);
	
	// NVIDIA-aligned color palette to match GPU Metrics component
	const nvidiaColors = {
		emerald: '#76c893',
		keppel: '#52b69a', 
		verdigris: '#34a0a4',
		bondiBlue: '#168aad',
		cerulean: '#1a759f',
		lapisLazuli: '#1e6091',
		indigoDye: '#184e77'
	};
	
	// Get logs from Redux store to parse deployment information
	const logs = useSelector(({ clusters }) => clusters.cluster.logs);
	
	// Get nodes directly from Redux store for real-time updates
	const nodesFromRedux = useSelector(({ clusters }) => clusters.cluster.nodes);
	
	// Get cluster metrics from Redux store for GPU information
	const clusterMetrics = useSelector(({ clusters }) => clusters.cluster.clusterMetrics?.data);
	
	// Use nodes from Redux if available, otherwise fall back to props
	const currentNodes = nodesFromRedux || nodes;
	
	// GPU detection helper function (legacy - uses healthData)
	const getGpuInfo = (healthData) => {
		if (!healthData?.system?.gpu?.available) {
			return null;
		}
		
		return {
			hasGpu: true,
			count: healthData.system.gpu.count || 1,
			model: healthData.system.gpu.model || 'GPU',
			utilization: healthData.system.gpu.utilization || 0,
			temperature: healthData.system.gpu.temperature || null,
			gpus: healthData.system.gpu.gpus || []
		};
	};
	
	// New GPU detection function using clusterMetrics
	const getGpuInfoFromMetrics = (hostname) => {
		if (!clusterMetrics?.nodes || !Array.isArray(clusterMetrics.nodes)) {
			return null;
		}
		
		// Find the node with matching hostname
		const nodeMetrics = clusterMetrics.nodes.find(node => 
			(node.hostname === hostname) || 
			(node.nodeName === hostname) || 
			(node.name === hostname) || 
			(node.host === hostname)
		);
		
		if (!nodeMetrics?.system?.gpu?.available || !nodeMetrics.system.gpu.gpus?.length) {
			return null;
		}
		
		const gpuData = nodeMetrics.system.gpu;
		return {
			hasGpu: true,
			count: gpuData.gpus?.length || gpuData.count || 1,
			model: gpuData.gpus?.[0]?.name || gpuData.model || 'GPU',
			utilization: gpuData.gpus?.reduce((sum, gpu) => sum + (gpu.utilizationPercent || 0), 0) / (gpuData.gpus?.length || 1) || 0,
			temperature: gpuData.gpus?.reduce((sum, gpu) => sum + (gpu.temperatureC || 0), 0) / (gpuData.gpus?.length || 1) || null,
			gpus: gpuData.gpus || [],
			totalMemoryMB: gpuData.gpus?.reduce((sum, gpu) => sum + (gpu.memory?.totalMB || 0), 0) || 0,
			usedMemoryMB: gpuData.gpus?.reduce((sum, gpu) => sum + (gpu.memory?.usedMB || 0), 0) || 0
		};
	};
	
	// Helper function to clear all tooltips and popovers
	const clearAllTooltipsAndPopovers = () => {
		// Clear timeouts using refs
		if (popoverTimeout.current) {
			clearTimeout(popoverTimeout.current);
			popoverTimeout.current = null;
		}
		if (hoverTimeout.current) {
			clearTimeout(hoverTimeout.current);
			hoverTimeout.current = null;
		}
		if (tooltipTimeout.current) {
			clearTimeout(tooltipTimeout.current);
			tooltipTimeout.current = null;
		}
		
		// Clear React tooltip state
		setTooltip(null);
		
		// Clear DOM tooltips
		d3.selectAll(".topology-tooltip").remove();
	};

	// Popover handlers - click-based to avoid hover conflicts with node tooltips
	const handlePopoverOpen = (event, gpuData, nodeData = null) => {
		// Clear any existing timeouts and tooltips
		if (hoverTimeout.current) {
			clearTimeout(hoverTimeout.current);
			hoverTimeout.current = null;
		}
		if (tooltipTimeout.current) {
			clearTimeout(tooltipTimeout.current);
			tooltipTimeout.current = null;
		}
		
		// Clear any existing tooltips
		setTooltip(null);
		d3.selectAll(".topology-tooltip").remove();
		
		// Open GPU popover on click
		setPopoverAnchorEl(event.currentTarget);
		setSelectedGpuData(gpuData);
		setSelectedNodeData(nodeData);
	};

	const handlePopoverClose = () => {
		// Close popover immediately for click-based interaction
		setPopoverAnchorEl(null);
		setSelectedGpuData(null);
		setSelectedNodeData(null);
	};

	// Auto-load logs when component mounts to get deployment information
	useEffect(() => {
		if (cluster?.id && !logs) {
			// Try to get deployment logs which should contain connector installations
			dispatch(ClusterActions.getLogs({ id: cluster.id, type: 'deploy' }));
		}
	}, [cluster?.id, logs, dispatch]);
	
	// Auto-load cluster metrics when component mounts to get GPU information
	useEffect(() => {
		if (cluster?.id && !clusterMetrics) {
			dispatch(ClusterActions.fetchClusterMetrics(cluster.id));
		}
	}, [cluster?.id, clusterMetrics, dispatch]);
	
	// Cleanup timeouts on unmount
	useEffect(() => {
		return () => {
			if (popoverTimeout.current) {
				clearTimeout(popoverTimeout.current);
			}
			if (hoverTimeout.current) {
				clearTimeout(hoverTimeout.current);
			}
			if (tooltipTimeout.current) {
				clearTimeout(tooltipTimeout.current);
			}
		};
	}, []); // Empty dependency array since we're using refs
	
	// Add debugging for logs content
	useEffect(() => {
		if (logs) {
			// Split by DEPLOYMENT: to separate individual log entries
			const deploymentEntries = logs.split('[2025-').filter(entry => entry.trim());			
			
		}
	}, [logs]);
	
	// Parse deployment logs to extract node types, volumes, and media information
	const parseDeploymentInfo = (logs) => {
		if (!logs) {
			return { nodeTypes: {}, volumes: [], mediaNodes: [] };
		}

		const nodeTypes = {};
		const volumes = [];
		const mediaNodes = [];

		try {
			// Strip HTML tags from logs for better parsing
			const cleanLogs = logs.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ');
			
			
			// Find all connector installations using regex
			const connectorMatches = cleanLogs.matchAll(/(?:\[[^\]]+\]\s+)?(?:DEPLOYMENT:\s+)?Node\s+([\w.-]+):\s+SUCCESS\s+-\s+([\w-]+)-connector-install/g);
			
			for (const match of connectorMatches) {
				const [, nodeName, connectorType] = match;
				nodeTypes[nodeName] = connectorType; // 'application', 'media', 'head'
				
				// Also add to mediaNodes if it's a media connector
				if (connectorType === 'media' && !mediaNodes.includes(nodeName)) {
					mediaNodes.push(nodeName);
				}
			}
			
			// Find all volume attachments using regex
			const volumeMatches = cleanLogs.matchAll(/(?:\[[^\]]+\]\s+)?(?:DEPLOYMENT:\s+)?Volume:\s+([\w-]+)\s+\((\d+)GB\)\s+attached\s+to\s+([\w.-]+)/g);
			
			// Keep track of volume counts per node for proper indexing
			const volumeCountPerNode = {};
			
			for (const match of volumeMatches) {
				const [, volumeId, size, nodeName] = match;
				
				// Track volume index for this node
				if (!volumeCountPerNode[nodeName]) {
					volumeCountPerNode[nodeName] = 0;
				}
				const volumeIndexForNode = volumeCountPerNode[nodeName];
				volumeCountPerNode[nodeName]++;
				
				// Get performance data for this volume based on the node it's attached to
				const performanceData = getVolumePerformanceData(volumeId, cluster, nodeName);
				
				// Get volume info (name and mount path) from cluster configuration
				const volumeInfo = getVolumeInfoFromCluster(volumeId, cluster, nodeName, volumeIndexForNode);
				
				volumes.push({
					id: volumeId,
					size: size,
					attachedTo: nodeName,
					performanceData: performanceData,
					volumeName: volumeInfo?.volumeName || volumeId, // Use extracted name or fallback to ID
					mountPath: volumeInfo?.mountPath || null // Include mount path
				});
			}
		} catch (error) {
			console.error("Error parsing deployment logs:", error);
		}

		
		return { nodeTypes, volumes, mediaNodes };
	};

	// Helper function to extract volume name from mount path
	const getVolumeInfoFromCluster = (volumeId, cluster, attachedNodeName, volumeIndex = 0) => {
		try {
			if (!cluster?.data?.node?.group || !attachedNodeName) return null;
			
			// Parse node name to get group index: ac-<string>-<group_index>-<node_count>
			const nodeNameMatch = attachedNodeName.match(/ac-.*-(\d+)-\d+$/);
			if (!nodeNameMatch) {
				return null;
			}
			
			const groupIndex = parseInt(nodeNameMatch[1]);
			
			// Get the corresponding group
			const targetGroup = cluster.data.node.group[groupIndex];
			if (!targetGroup) {
				return null;
			}
			
			// Search through attached volumes in this group to find matching volume
			if (targetGroup.attachedVolumes) {
				// First, try to find exact ID match
				for (const volume of targetGroup.attachedVolumes) {
					if (volume.id && volume.id === volumeId) {
						// Extract volume name from mount path (e.g., "/mnt/storage-2-1" -> "storage-2-1")
						const mountPath = volume.mountPath;
						let volumeName = volumeId; // fallback to volumeId
						
						if (mountPath) {
							const pathMatch = mountPath.match(/\/mnt\/(.+)$/);
							if (pathMatch) {
								volumeName = pathMatch[1]; // e.g., "storage-2-1"
							}
						}
						
						return {
							volumeName: volumeName,
							mountPath: mountPath,
							size: volume.size || null
						};
					}
				}
				
				// If no exact match, try to match by index (for cases where IDs don't match exactly)
				// This is a fallback mechanism for when deployment log volume IDs don't match cluster config IDs
				if (targetGroup.attachedVolumes.length > volumeIndex) {
					const volume = targetGroup.attachedVolumes[volumeIndex];
					
					if (volume && volume.mountPath) {
						const mountPath = volume.mountPath;
						const pathMatch = mountPath.match(/\/mnt\/(.+)$/);
						const volumeName = pathMatch ? pathMatch[1] : volumeId;
						
						return {
							volumeName: volumeName,
							mountPath: mountPath,
							size: volume.size || null
						};
					}
				}
			}
		} catch (error) {
			console.error("Error extracting volume info from cluster data:", error);
		}
		return null;
	};

	// Function to extract performance data from cluster configuration
	const getVolumePerformanceData = (volumeId, cluster, attachedNodeName) => {
		try {
			if (!cluster?.data?.node?.group || !attachedNodeName) return null;
			
			// Parse node name to get group index: ac-<string>-<group_index>-<node_count>
			const nodeNameMatch = attachedNodeName.match(/ac-.*-(\d+)-\d+$/);
			if (!nodeNameMatch) {
				return null;
			}
			
			const groupIndex = parseInt(nodeNameMatch[1]);
			
			// Get the corresponding group
			const targetGroup = cluster.data.node.group[groupIndex];
			if (!targetGroup) {
				return null;
			}
			
			
			// Search through attached volumes in this group
			if (targetGroup.attachedVolumes) {
				for (const volume of targetGroup.attachedVolumes) {
					if (volume.performanceOverrides) {
						return {
							readIops: volume.performanceOverrides.readIops,
							writeIops: volume.performanceOverrides.writeIops,
							readBandwidth: volume.performanceOverrides.readBandwidth,
							writeBandwidth: volume.performanceOverrides.writeBandwidth,
							latencyRead: volume.performanceOverrides.latencyRead,
							latencyWrite: volume.performanceOverrides.latencyWrite
						};
					}
				}
			}
		} catch (error) {
			console.error("Error extracting volume performance data:", error);
		}
		return null;
	};

	useEffect(() => {
		// Clear any pending update
		if (updateTimeoutRef.current) {
			clearTimeout(updateTimeoutRef.current);
		}
		
		const now = Date.now();
		const timeSinceLastInteraction = now - lastInteractionTimeRef.current;
		
		// If user has interacted recently (within 1 second), delay the update more aggressively
		if (isUserInteractingRef.current || timeSinceLastInteraction < 1000) {
			updateTimeoutRef.current = setTimeout(() => {
				// Double-check if user is still interacting before updating
				if (!isUserInteractingRef.current && (Date.now() - lastInteractionTimeRef.current) >= 1000) {
					createScalableTopology();
				} else {
					// If still interacting, try again later
					updateTimeoutRef.current = setTimeout(() => {
						createScalableTopology();
					}, 1000);
				}
			}, 1000); // Wait 1 second instead of 500ms
		} else {
			// Safe to update immediately (but not during height changes from scroll)
			if (!isUserInteractingRef.current) {
				createScalableTopology();
			}
		}
		
		// Cleanup timeout on unmount
		return () => {
			if (updateTimeoutRef.current) {
				clearTimeout(updateTimeoutRef.current);
			}
		};
	}, [currentNodes, viewMode, healthData, logs, cluster, clusterMetrics, dynamicHeight]);

	// Handle window resize to update canvas width
	useEffect(() => {
		const handleResize = () => {
			// Debounce resize to avoid too many updates
			if (updateTimeoutRef.current) {
				clearTimeout(updateTimeoutRef.current);
			}
			updateTimeoutRef.current = setTimeout(() => {
				if (!isUserInteractingRef.current) {
					createScalableTopology();
				}
			}, 250);
		};

		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	// Handle scroll to dynamically adjust canvas height
	useEffect(() => {
		const handleScroll = () => {
			if (!containerRef.current) return;
			
			const container = containerRef.current;
			const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
			const containerTop = container.getBoundingClientRect().top + scrollTop;
			const containerScrollOffset = Math.max(0, scrollTop - containerTop);
			
			// Calculate dynamic height based on scroll
			const baseHeight = 600; // Minimum height
			const maxHeight = 900; // Maximum height
			const scrollMultiplier = 0.5; // How much height grows per pixel scrolled
			
			const newHeight = Math.min(maxHeight, baseHeight + (containerScrollOffset * scrollMultiplier));
			
			if (Math.abs(newHeight - dynamicHeight) > 10) { // Only update if significant change
				setDynamicHeight(newHeight);
			}
		};

		// Throttle scroll events
		let scrollTimeout;
		const throttledScroll = () => {
			if (scrollTimeout) return;
			scrollTimeout = setTimeout(() => {
				handleScroll();
				scrollTimeout = null;
			}, 16); // ~60fps
		};

		window.addEventListener('scroll', throttledScroll);
		return () => {
			window.removeEventListener('scroll', throttledScroll);
			if (scrollTimeout) clearTimeout(scrollTimeout);
		};
	}, [dynamicHeight]);


	const createScalableTopology = () => {
		const svg = d3.select(svgRef.current);
		
		// More robust way to store current zoom/pan state before clearing
		let preservedTransform = currentTransformRef.current;
		if (svgRef.current && svgRef.current.__zoom__) {
			try {
				const currentTransform = d3.zoomTransform(svgRef.current);
				// Only update if it's different from what we have stored
				if (currentTransform.k !== currentTransformRef.current.k || 
					currentTransform.x !== currentTransformRef.current.x || 
					currentTransform.y !== currentTransformRef.current.y) {
					preservedTransform = currentTransform;
					currentTransformRef.current = currentTransform;
				}
			} catch (e) {
				// Fallback to stored transform if getting current fails
				preservedTransform = currentTransformRef.current;
			}
		}
		
		svg.selectAll("*").remove();

		// Calculate dynamic width based on container size minus space for controls
		const containerWidth = containerRef.current ? containerRef.current.clientWidth : 900;
		const controlsWidth = 60; // Approximate width needed for zoom controls (buttons + padding)
		const width = Math.max(400, containerWidth - controlsWidth); // Minimum width of 400px
		const height = dynamicHeight; // Use dynamic height based on scroll
		const computeNodes = currentNodes?.data || [];
		const nodeCount = computeNodes.length;


		svg.attr("width", width).attr("height", height);

		// Create the main group element first
		const g = svg.append("g");

		// Add zoom and pan functionality
		const zoom = d3.zoom()
			.scaleExtent([0.1, 5])
			.on("start", () => {
				// Mark that user is actively interacting
				isUserInteractingRef.current = true;
				lastInteractionTimeRef.current = Date.now();
			})
			.on("zoom", (event) => {
				g.attr("transform", event.transform);
				setZoomLevel(event.transform.k);
				// Always store current transform and update interaction time
				currentTransformRef.current = event.transform;
				lastInteractionTimeRef.current = Date.now();
			})
			.on("end", () => {
				// Mark that user has stopped interacting with a delay
				lastInteractionTimeRef.current = Date.now();
				setTimeout(() => {
					isUserInteractingRef.current = false;
				}, 200); // Increased delay to ensure interaction has truly ended
			});

		svg.call(zoom);
		
		// Restore previous zoom/pan state using preserved transform
		if (!isUserInteractingRef.current && preservedTransform && 
			(preservedTransform.k !== 1 || preservedTransform.x !== 0 || preservedTransform.y !== 0)) {
			// Use immediate transform without animation to avoid conflicts
			svg.call(zoom.transform, preservedTransform);
		}

		// Store zoom behavior for button controls
		svgRef.current.__zoom__ = zoom;

		// Create adaptive layout based on cluster size for scalability
		let topologyData;
		
		if (nodeCount <= 20) {
			// Small clusters: Show individual compute nodes
			topologyData = createSmallClusterLayout(nodes, width, height, cluster);
		} else if (nodeCount <= 200) {
			// Medium clusters: Group into racks
			topologyData = createMediumClusterLayout(nodes, width, height, cluster);
		} else {
			// Large clusters (200+ nodes): Group into cabinets/racks for scalability
			topologyData = createLargeClusterLayout(nodes, width, height, cluster);
		}

		renderTopology(g, topologyData, width, height);
	};

	const createSmallClusterLayout = (nodes, width, height, cluster) => {
		// Build compute nodes list from cluster.data.node.group[] structure
		const computeNodes = [];
		
		if (cluster?.data?.node?.group) {
			// Get cluster ID suffix for hostname construction
			const clusterId = cluster._id || cluster.id;
			const clusterIdSuffix = clusterId ? clusterId.toString().slice(-4) : '';
			
			cluster.data.node.group.forEach((group, groupIndex) => {
				for (let nodeIndex = 0; nodeIndex < group.count; nodeIndex++) {
					// Construct hostname: ac-{clusterId}-{groupIndex}-{nodeIndex}
					const hostname = `ac-${clusterIdSuffix}-${groupIndex}-${nodeIndex}`;
					
					computeNodes.push({
						name: hostname,
						hostname: hostname,
						groupIndex: groupIndex,
						nodeIndex: nodeIndex,
						group: group,
						instance_id: group.instance_id,
						nodeType: group.nodeType,
						isMediaNode: group.nodeType === 'media',
						attachedVolumes: group.attachedVolumes || [],
						state: { state: 'running', color: 'green', icon: 'check_circle' } // Default state
					});
				}
			});
		}
		
		// Parse deployment information from logs for additional metadata
		const deploymentInfo = parseDeploymentInfo(logs);
		
		// Sort compute nodes by hostname convention: ac-<string>-<group>-<count>
		// Sort by group first (ascending), then by count (ascending)
		const sortedComputeNodes = [...computeNodes].sort((a, b) => {
			const getHostnameParts = (hostname) => {
				const match = hostname?.match(/^ac-.*-(\d+)-(\d+)$/);
				if (match) {
					return {
						group: parseInt(match[1], 10),
						count: parseInt(match[2], 10)
					};
				}
				// Fallback for non-standard hostnames
				return { group: 999, count: 999 };
			};
			
			const partsA = getHostnameParts(a.hostname || a.name);
			const partsB = getHostnameParts(b.hostname || b.name);
			
			// Sort by group first, then by count
			if (partsA.group !== partsB.group) {
				return partsA.group - partsB.group;
			}
			return partsA.count - partsB.count;
		});
		
		// Basic HPC infrastructure components with VCN subnet instead of physical switches
		// Get head node attached volumes from cluster configuration (it's at cluster.data.server, not cluster.data.node.server)
		const headNodeAttachedVolumes = cluster?.data?.server?.attachedVolumes || [];

		const baseTopology = {
			nodes: [
				{ 
					id: "head", 
					type: "head", 
					name: "Head Node", 
					x: width/2, 
					y: 100, 
					level: 0, 
					status: "healthy",
					clusterName: cluster?.name || "head-node",
					ip: cluster?.info?.terraform?.publicIp || "",
					privateIp: cluster?.info?.terraform?.privateIp || "",
					instanceShape: cluster?.data?.server?.instance_id || "",
					hostname: cluster?.name || "head-node",  // Add hostname for GPU lookup
					gpuInfo: getGpuInfoFromMetrics(cluster?.name || "head-node"),
					attachedVolumes: headNodeAttachedVolumes.map(vol => {
						// Extract volume name from mount path
						// For VolumeZ: /mnt/storage-head -> "storage-head"
						// For IBM: /shared_storage -> "shared_storage"
						let volumeName = vol.id;
						if (vol.mountPath) {
							// Remove leading slashes and /mnt/ prefix
							volumeName = vol.mountPath.replace(/^\/mnt\//, '').replace(/^\//, '');
						}
						
						// Get size - support both deployed (size/capacity fields) and pending
						// For IBM volumes, check overrides.capacity as fallback (Heidi AI storage)
						let displaySize = vol.size || vol.capacity;
						if (!displaySize && vol.overrides && vol.overrides.capacity) {
							displaySize = vol.overrides.capacity;
						}
						// Don't use volumeName as fallback - keep it undefined if not available
						
						// Get IOPS if available from overrides (IBM Heidi AI storage)
						let displayIops = null;
						if (vol.overrides && vol.overrides.iops) {
							displayIops = vol.overrides.iops;
						}
						
						return {
							id: vol.id,
							size: displaySize, // Will be undefined for pending volumes
							iops: displayIops, // IOPS for IBM volumes
							volumeName: volumeName,
							mountPath: vol.mountPath,
							status: vol.status // Include status to check if pending
						};
					})
				},
				{ 
					id: "vcn-subnet", 
					type: "subnet", 
					name: "VCN Subnet", 
					subnet: cluster?.subnet || "10.0.0.0/24",
					x: width/2, 
					y: 180, 
					level: 1, 
					status: "healthy" 
				},
				{ id: "storage", type: "storage", name: "Shared Storage", x: width/2 + 140, y: 180, level: 1, status: "healthy" },
				{ id: "internet-cloud", type: "cloud", name: "Internet", x: width/2 - 100, y: 100, level: 0.5, status: "healthy" },
				{ id: "management", type: "management", name: "Heidi Management", x: width/2 - 200, y: 60, level: 1, status: "healthy" }
			],
			links: [
				{ source: "head", target: "vcn-subnet" },
				{ source: "head", target: "storage" },
				{ source: "head", target: "internet-cloud" },
				{ source: "internet-cloud", target: "management" }
			],
			racks: []
		};

		// Add compute nodes if they exist with real node data - positioned closer to head node
		if (sortedComputeNodes.length > 0) {
			const nodesPerRow = 5;
			const nodeSpacing = 120;
			
			const computeNodesData = sortedComputeNodes.map((node, i) => {
				const row = Math.floor(i / nodesPerRow);
				const posInRow = i % nodesPerRow;
				
				// Calculate how many nodes are in this row
				const remainingNodes = sortedComputeNodes.length - (row * nodesPerRow);
				const nodesInThisRow = Math.min(nodesPerRow, remainingNodes);
				
				// Center the nodes in this row
				const rowWidth = (nodesInThisRow - 1) * nodeSpacing;
				const rowStartX = (width - rowWidth) / 2;
				
				// Get node metadata from cluster data structure
				const nodeName = node.hostname || node.name;
				const isMediaNode = node.isMediaNode || node.nodeType === 'media';
				
				// Get attached volumes from database (node.attachedVolumes from cluster.data.node.group[])
				const attachedVolumes = (node.attachedVolumes || []).map(vol => {
					// Extract volume name from mount path
					// For VolumeZ: /mnt/storage-2-1 -> "storage-2-1"
					// For IBM: /data -> "data", /scratch -> "scratch"
					let volumeName = vol.id;
					if (vol.mountPath) {
						// Remove leading slashes and /mnt/ prefix
						volumeName = vol.mountPath.replace(/^\/mnt\//, '').replace(/^\//, '');
					}
					
					// Get size - support both deployed (size/capacity fields) and pending (show as pending)
					// For IBM volumes, check overrides.capacity as fallback (Heidi AI storage)
					let displaySize = vol.size || vol.capacity;
					if (!displaySize && vol.overrides && vol.overrides.capacity) {
						displaySize = vol.overrides.capacity;
					}
					// Don't use volumeName as size fallback - keep it undefined if not available
					
					// Get IOPS if available from overrides (IBM Heidi AI storage)
					let displayIops = null;
					if (vol.overrides && vol.overrides.iops) {
						displayIops = vol.overrides.iops;
					}
					
					return {
						id: vol.id,
						size: displaySize, // Will be undefined for pending volumes
						iops: displayIops, // IOPS for IBM volumes
						volumeName: volumeName,
						mountPath: vol.mountPath,
						status: vol.status, // Include status to check if pending
						performanceData: vol.performanceOverrides || null
					};
				});
				
				// Fallback to deployment logs for connector type if not in database
				const nodeType = deploymentInfo.nodeTypes[nodeName] || node.nodeType;
				
				// Extract EFA information from cluster node groups
				const getEfaInfo = (nodeName) => {
					if (!cluster?.data?.node?.group) return false;
					
					// Parse node name to get group index: ac-<string>-<group_index>-<node_count>
					const nodeNameMatch = nodeName.match(/^ac-.*-(\d+)-\d+$/);
					if (!nodeNameMatch) return false;
					
					const groupIndex = parseInt(nodeNameMatch[1]);
					const targetGroup = cluster.data.node.group[groupIndex];
					return targetGroup?.efa || false;
				};
				
				// Check for Azure proximity placement (InfiniBand)
				const getProximityPlacementInfo = () => {
					return cluster?.data?.network?.use_proximity_placement || false;
				};
				
				const hasEfa = getEfaInfo(nodeName);
				const hasProximityPlacement = getProximityPlacementInfo();
				
				// Determine the visual node type
				let visualType = "compute"; // default
				if (nodeType === "application") {
					visualType = "application";
				} else if (nodeType === "media" || isMediaNode) {
					visualType = "media";
				}
				
				
				return {
					id: `node-${i}`,
					type: visualType,
					name: node.name || `Compute-${i+1}`,
					hostname: node.name,
					ip: node.instance_id || "",
					state: node.state?.state || "unknown",
					stateColor: node.state?.color || "grey",
					stateIcon: node.state?.icon || "device_hub",
					x: rowStartX + posInRow * nodeSpacing,
					y: 280 + row * 100, // Reduced row spacing between compute nodes (was 140)
					data: node,
					level: 2,
					status: node.state?.state?.toLowerCase() || "unknown",
					attachedVolumes: attachedVolumes, // Add volume information
					isMediaNode: isMediaNode,
					nodeConnectorType: nodeType,
					hasEfa: hasEfa, // Add EFA information
					hasProximityPlacement: hasProximityPlacement, // Add proximity placement (InfiniBand) information
					gpuInfo: getGpuInfoFromMetrics(node.name) // Get GPU info from metrics
				};
			});

			baseTopology.nodes.push(...computeNodesData);
			
			// Check if any nodes have EFA or proximity placement enabled
			const hasEfaOrProximity = computeNodesData.some(node => node.hasEfa || node.hasProximityPlacement);
			
			// Add placement group container if EFA or proximity placement is enabled
			if (hasEfaOrProximity) {
				// Calculate bounds for all compute nodes
				const nodeXPositions = computeNodesData.map(node => node.x);
				const nodeYPositions = computeNodesData.map(node => node.y);
				const minX = Math.min(...nodeXPositions);
				const maxX = Math.max(...nodeXPositions);
				const minY = Math.min(...nodeYPositions);
				const maxY = Math.max(...nodeYPositions);
				
				// Add padding around the nodes
				const padding = 40;
				const containerWidth = (maxX - minX) + padding * 2 + 50; // Extra width for node sizes
				const containerHeight = (maxY - minY) + padding * 2 + 36; // Extra height for node sizes
				const containerX = (minX + maxX) / 2; // Center X
				const containerY = (minY + maxY) / 2; // Center Y
				
				const placementGroup = {
					id: "placement-group",
					type: "placement-group-container",
					name: "Placement Group",
					x: containerX,
					y: containerY,
					width: containerWidth,
					height: containerHeight,
					level: 1.8, // Between network junction and compute nodes
					status: "healthy"
				};
				baseTopology.nodes.push(placementGroup);
			}
			
			// Add attached volume nodes for compute nodes - positioned under nodes
			const volumesByNode = {};
			
			// Group volumes by attached node from database data (already in computeNodesData)
			computeNodesData.forEach(node => {
				if (node.attachedVolumes && node.attachedVolumes.length > 0) {
					volumesByNode[node.hostname] = node.attachedVolumes;
				}
			});
			
			// Create volume nodes positioned under their parent nodes
			Object.entries(volumesByNode).forEach(([nodeName, volumes]) => {
				const attachedNode = computeNodesData.find(n => n.hostname === nodeName);
				if (attachedNode) {
					volumes.forEach((volume, volumeIndex) => {
						// Dynamic volume spacing based on number of volumes to prevent overlap
						const nodeSpacing = 120; // Distance between compute nodes
						const maxVolumeWidth = nodeSpacing * 0.8; // Use 80% of node spacing to prevent overlap
						const volumeSpacing = volumes.length > 1 ? Math.min(60, maxVolumeWidth / (volumes.length - 1)) : 0;
						const totalVolumesWidth = (volumes.length - 1) * volumeSpacing;
						const startX = attachedNode.x - totalVolumesWidth / 2;
						
						baseTopology.nodes.push({
							id: `volume-${attachedNode.id}-${volumeIndex}`,
							type: "attached-volume",
							name: volume.size,
							volumeInfo: volume.volumeName || `Vol-${volumeIndex + 1}`, // Use actual volume name
							volumeId: volume.id,
							size: volume.size,
							iops: volume.iops, // Include IOPS for IBM volumes
							attachedTo: volume.attachedTo,
							mountPath: volume.mountPath, // Add mount path for popover
							performanceData: volume.performanceData, // Add performance data
							status: volume.status, // Include status to check deployment state
							x: startX + volumeIndex * volumeSpacing,
							y: attachedNode.y + 45, // Reduced spacing between compute and volume (was 80)
							level: 2.5
						});
						
						// Add link from node to volume
						baseTopology.links.push({
							source: attachedNode.id,
							target: `volume-${attachedNode.id}-${volumeIndex}`
						});
					});
				}
			});
			
			// Add head node volumes if they exist
			const headNode = baseTopology.nodes.find(n => n.id === "head");
			if (headNode && headNode.attachedVolumes && headNode.attachedVolumes.length > 0) {
				
				// Position head node volume to the right of the head node (not below)
				const volume = headNode.attachedVolumes[0]; // Using first volume for NFS
				const volumeX = headNode.x + 100; // Position to the right of head node
				const volumeY = headNode.y; // Same Y level as head node
				
				baseTopology.nodes.push({
					id: `volume-head-0`,
					type: "attached-volume",
					name: volume.size,
					volumeInfo: volume.volumeName || `Vol-1`,
					volumeId: volume.id,
					size: volume.size,
					attachedTo: "head",
					mountPath: volume.mountPath,
					x: volumeX,
					y: volumeY,
					level: 0.5,
					status: "healthy"
				});
				
				// Add link from head node to volume
				baseTopology.links.push({
					source: "head",
					target: `volume-head-0`
				});
				
				// Find and update the storage (NFS) link to hang from the volume instead of head node
				const storageNode = baseTopology.nodes.find(n => n.id === "storage");
				if (storageNode) {
					// Update storage node position to be below the volume
					storageNode.x = volumeX;
					storageNode.y = volumeY + 80; // Position below the volume
					
					// Remove the original head->storage link and replace with volume->storage link
					const headToStorageLinkIndex = baseTopology.links.findIndex(
						link => link.source === "head" && link.target === "storage"
					);
					if (headToStorageLinkIndex !== -1) {
						baseTopology.links.splice(headToStorageLinkIndex, 1);
					}
					
					// Add new link from volume to storage
					baseTopology.links.push({
						source: `volume-head-0`,
						target: "storage"
					});
				}
			}
			
			// Create network branching structure with invisible junction points
			if (computeNodesData.length > 0) {
				// Add main trunk junction point (invisible)
				const mainJunction = {
					id: "main-junction",
					type: "invisible-junction",
					name: "Network Junction",
					x: width / 2,
					y: hasEfaOrProximity ? 250 : 230, // Position lower if placement group exists
					level: 1.5,
					status: "healthy"
				};
				baseTopology.nodes.push(mainJunction);
				
				// Connect subnet to main junction (placement group is just visual container)
				baseTopology.links.push({ source: "vcn-subnet", target: "main-junction" });
				
				// Group nodes by rows and create distribution points for each row
				const rows = {};
				computeNodesData.forEach((node, i) => {
					const row = Math.floor(i / nodesPerRow);
					if (!rows[row]) rows[row] = [];
					rows[row].push(node);
				});
				
				// Create distribution points for each row
				Object.keys(rows).forEach(rowIndex => {
					const rowNodes = rows[rowIndex];
					const rowY = 280 + parseInt(rowIndex) * 100; // Updated to match new row spacing (was 140)
					
					if (rowNodes.length > 1) {
						// Create an invisible distribution junction for this row
						const rowJunction = {
							id: `row-junction-${rowIndex}`,
							type: "invisible-junction",
							name: `Row ${parseInt(rowIndex) + 1} Distribution`,
							x: width / 2,
							y: rowY - 25, // Slightly above the compute nodes
							level: 1.8,
							status: "healthy"
						};
						baseTopology.nodes.push(rowJunction);
						
						// Connect main junction to row junction
						baseTopology.links.push({ source: "main-junction", target: `row-junction-${rowIndex}` });
						
						// Connect row junction to all nodes in this row
						rowNodes.forEach(node => {
							baseTopology.links.push({ source: `row-junction-${rowIndex}`, target: node.id });
						});
					} else {
						// Single node in row - connect directly to main junction
						baseTopology.links.push({ source: "main-junction", target: rowNodes[0].id });
					}
				});
			}
			
			// Add EFA connections between nodes with EFA enabled
			const efaNodes = computeNodesData.filter(node => node.hasEfa);
			if (efaNodes.length > 1) {
				// Create EFA connections between all EFA-enabled nodes (full mesh)
				for (let i = 0; i < efaNodes.length; i++) {
					for (let j = i + 1; j < efaNodes.length; j++) {
						baseTopology.links.push({
							source: efaNodes[i].id,
							target: efaNodes[j].id,
							type: "efa", // Special type for EFA connections
							color: "#2196F3" // Blue color for EFA connections
						});
					}
				}
			}
			
			// Add InfiniBand connections between nodes with proximity placement enabled
			const proximityNodes = computeNodesData.filter(node => node.hasProximityPlacement);
			if (proximityNodes.length > 1) {
				// Create InfiniBand connections between all proximity placement nodes (full mesh)
				for (let i = 0; i < proximityNodes.length; i++) {
					for (let j = i + 1; j < proximityNodes.length; j++) {
						baseTopology.links.push({
							source: proximityNodes[i].id,
							target: proximityNodes[j].id,
							type: "infiniband", // Special type for InfiniBand connections
							color: "#2196F3" // Blue color for InfiniBand connections
						});
					}
				}
			}
		}

		return baseTopology;
	};

	const createMediumClusterLayout = (nodes, width, height, cluster) => {
		// Rack-based grouping for medium clusters
		const computeNodes = currentNodes?.data || [];
		
		// Sort compute nodes by hostname convention: ac-<string>-<group>-<count>
		const sortedComputeNodes = [...computeNodes].sort((a, b) => {
			const getHostnameParts = (hostname) => {
				const match = hostname?.match(/^ac-.*-(\d+)-(\d+)$/);
				if (match) {
					return {
						group: parseInt(match[1], 10),
						count: parseInt(match[2], 10)
					};
				}
				return { group: 999, count: 999 };
			};
			
			const partsA = getHostnameParts(a.hostname || a.name);
			const partsB = getHostnameParts(b.hostname || b.name);
			
			if (partsA.group !== partsB.group) {
				return partsA.group - partsB.group;
			}
			return partsA.count - partsB.count;
		});
		
		const rackSize = 8;
		const racks = [];
		
		for (let i = 0; i < sortedComputeNodes.length; i += rackSize) {
			const rackNodes = sortedComputeNodes.slice(i, i + rackSize);
			const rackId = Math.floor(i / rackSize);
			const racksPerRow = Math.min(6, Math.ceil(Math.sqrt(Math.ceil(sortedComputeNodes.length / rackSize))));
			
			const rackX = 100 + (rackId % racksPerRow) * 130;
			const rackY = 280 + Math.floor(rackId / racksPerRow) * 120;
			
			// Calculate rack health based on node states
			const healthyNodes = rackNodes.filter(n => n.state?.state === 'Available' || n.state?.state === 'Free');
			const busyNodes = rackNodes.filter(n => n.state?.state === 'Running' || n.state?.state === 'Busy');
			const downNodes = rackNodes.filter(n => n.state?.state === 'Down' || n.state?.state === 'Offline');
			
			racks.push({
				id: `rack-${rackId}`,
				name: `Rack ${rackId + 1}`,
				x: rackX,
				y: rackY,
				nodes: rackNodes,
				nodeCount: rackNodes.length,
				healthyCount: healthyNodes.length,
				busyCount: busyNodes.length,
				downCount: downNodes.length,
				avgLoad: 0 // Can be calculated from node data if available
			});
		}

		return {
			nodes: [
				{ 
					id: "head", 
					type: "head", 
					name: "Head Node", 
					x: width/2, 
					y: 60, 
					level: 0,
					clusterName: cluster?.name || "head-node",
					ip: cluster?.info?.terraform?.publicIp || "",
					privateIp: cluster?.info?.terraform?.privateIp || "",
					instanceShape: cluster?.data?.server?.instance_id || "",
					hostname: cluster?.name || "head-node",  // Add hostname for GPU lookup
					gpuInfo: getGpuInfoFromMetrics(cluster?.name || "head-node")
				},
				{ id: "management", type: "management", name: "Management", x: width/2 + 120, y: 140, level: 1 },
				{ id: "vcn-subnet", type: "subnet", name: "VCN Subnet", subnet: cluster?.subnet || "10.0.0.0/24", x: width/2, y: 140, level: 1 },
				{ id: "storage", type: "storage", name: "Storage", x: width/2 - 120, y: 140, level: 1 },
				...racks.map(rack => ({
					...rack,
					type: "rack",
					level: 2,
					status: rack.healthyCount === rack.nodeCount ? "healthy" : 
							rack.healthyCount > rack.nodeCount * 0.7 ? "warning" : "critical"
				}))
			],
			links: [],
			racks: racks
		};
	};

	const createLargeClusterLayout = (nodes, width, height, cluster) => {
		// Multi-level hierarchical layout for large clusters (up to 1000+ nodes)
		const computeNodes = currentNodes?.data || [];
		
		// Sort compute nodes by hostname convention: ac-<string>-<group>-<count>
		const sortedComputeNodes = [...computeNodes].sort((a, b) => {
			const getHostnameParts = (hostname) => {
				const match = hostname?.match(/^ac-.*-(\d+)-(\d+)$/);
				if (match) {
					return {
						group: parseInt(match[1], 10),
						count: parseInt(match[2], 10)
					};
				}
				return { group: 999, count: 999 };
			};
			
			const partsA = getHostnameParts(a.hostname || a.name);
			const partsB = getHostnameParts(b.hostname || b.name);
			
			if (partsA.group !== partsB.group) {
				return partsA.group - partsB.group;
			}
			return partsA.count - partsB.count;
		});
		
		const rackSize = 40; // Larger racks for efficiency
		const cabinetSize = 200; // More nodes per cabinet
		
		const cabinets = [];
		for (let i = 0; i < sortedComputeNodes.length; i += cabinetSize) {
			const cabinetNodes = sortedComputeNodes.slice(i, i + cabinetSize);
			const cabinetId = Math.floor(i / cabinetSize);
			const cabinetsPerRow = Math.min(6, Math.ceil(Math.sqrt(Math.ceil(sortedComputeNodes.length / cabinetSize))));
			
			const cabinetX = 120 + (cabinetId % cabinetsPerRow) * 140;
			const cabinetY = 300 + Math.floor(cabinetId / cabinetsPerRow) * 120;
			
			// Calculate cabinet health statistics
			const healthyNodes = cabinetNodes.filter(n => n.state?.state === 'Available' || n.state?.state === 'Free');
			const busyNodes = cabinetNodes.filter(n => n.state?.state === 'Running' || n.state?.state === 'Busy');
			const downNodes = cabinetNodes.filter(n => n.state?.state === 'Down' || n.state?.state === 'Offline');
			
			// Create racks within cabinet for organization
			const racks = [];
			for (let j = 0; j < cabinetNodes.length; j += rackSize) {
				const rackNodes = cabinetNodes.slice(j, j + rackSize);
				const rackId = Math.floor(j / rackSize);
				
				const rackHealthy = rackNodes.filter(n => n.state?.state === 'Available' || n.state?.state === 'Free');
				const rackBusy = rackNodes.filter(n => n.state?.state === 'Running' || n.state?.state === 'Busy');
				const rackDown = rackNodes.filter(n => n.state?.state === 'Down' || n.state?.state === 'Offline');
				
				racks.push({
					id: `cabinet-${cabinetId}-rack-${rackId}`,
					name: `R${rackId + 1}`,
					nodes: rackNodes,
					nodeCount: rackNodes.length,
					healthyCount: rackHealthy.length,
					busyCount: rackBusy.length,
					downCount: rackDown.length
				});
			}
			
			cabinets.push({
				id: `cabinet-${cabinetId}`,
				name: `Cabinet ${cabinetId + 1}`,
				x: cabinetX,
				y: cabinetY,
				racks: racks,
				totalNodes: cabinetNodes.length,
				healthyNodes: healthyNodes.length,
				busyNodes: busyNodes.length,
				downNodes: downNodes.length,
				avgLoad: 0 // Can be calculated if load data available
			});
		}

		return {
			nodes: [
				{ 
					id: "head", 
					type: "head", 
					name: "Head Node", 
					x: width/2, 
					y: 50, 
					level: 0,
					clusterName: cluster?.name || "head-node",
					ip: cluster?.info?.terraform?.publicIp || "",
					privateIp: cluster?.info?.terraform?.privateIp || "",
					instanceShape: cluster?.data?.server?.instance_id || "",
					hostname: cluster?.name || "head-node",  // Add hostname for GPU lookup
					gpuInfo: getGpuInfoFromMetrics(cluster?.name || "head-node")
				},
				{ id: "vcn-subnet", type: "subnet", name: "VCN Subnet", subnet: cluster?.subnet || "10.0.0.0/24", x: width/2, y: 120, level: 1 },
				{ id: "management", type: "management", name: "Management", x: width/2 - 150, y: 200, level: 2 },
				{ id: "storage-fabric", type: "storage", name: "Storage Fabric", x: width/2 + 150, y: 200, level: 2 },
				...cabinets.map(cabinet => ({
					...cabinet,
					type: "cabinet",
					level: 3,
					status: cabinet.healthyNodes === cabinet.totalNodes ? "healthy" : 
							cabinet.healthyNodes > cabinet.totalNodes * 0.8 ? "warning" : "critical"
				}))
			],
			links: [],
			cabinets: cabinets
		};
	};

	const renderTopology = (g, data, width, height) => {
		// Professional enterprise color scheme with solid light colors (no transparency)
		const nodeColors = {
			head: "#E3F2FD",           // Light blue (equivalent of rgba(25, 118, 210, 0.1))
			management: "#F3E5F5",     // Light purple (equivalent of rgba(123, 31, 162, 0.1))
			switch: "#E8F5E8",         // Light green (equivalent of rgba(56, 142, 60, 0.1))
			subnet: "#E3F2FD",         // Light blue for VCN subnet
			cloud: "#F5F5F5",          // Light gray for internet cloud
			junction: "#ECEFF1",       // Light blue-gray for network junctions
			"core-switch": "#E8F5E8",  // Light dark green
			storage: "#FFF3E0",        // Light orange
			rack: "#ECEFF1",           // Light gray
			cabinet: "#ECEFF1",        // Light dark gray
			compute: "#E8F5E8",        // Light green (default)
			application: "#E3F2FD",    // Light blue for application nodes
			media: "#FFF8E1",          // Light amber for media nodes (i4i instances)
			"attached-volume": "#F3E5F5", // Light purple for attached volumes
			"attached-volume-deploying": "#FFF3E0", // Light orange for deploying volumes
			"placement-group": "#E8F5E8", // Light green for placement group (old octagon)
			"placement-group-container": "rgba(232, 245, 232, 0.2)" // Very light and transparent green for placement group container
		};

		// State-based colors for compute nodes (solid colors)
		const stateColors = {
			// Available/Free states
			'Available': "#E8F5E8",   // Light green
			'Free': "#E8F5E8",        // Light green
			'Idle': "#E8F5E8",        // Light green
			
			// Busy states
			'Running': "#FFF3E0",     // Light orange
			'Busy': "#FFF3E0",        // Light orange
			'Mixed': "#FFF8E1",       // Light yellow
			'Allocated': "#FFF3E0",   // Light orange
			
			// Down/Problem states
			'Down': "#FFEBEE",        // Light red
			'Offline': "#F5F5F5",     // Light gray
			'Drained': "#EFEBE9",     // Light brown
			'Drain': "#EFEBE9",       // Light brown
			
			// Transitioning states
			'Deploying': "#E1F5FE",   // Light blue
			'Destroying': "#F3E5F5",  // Light purple
			
			// Unknown/Default
			'Unknown': "#F5F5F5",     // Light gray
			'unknown': "#F5F5F5"      // Light gray
		};

		// State-based border colors
		const stateBorderColors = {
			'Available': "#4CAF50", 'Free': "#4CAF50", 'Idle': "#4CAF50",
			'Running': "#FF9800", 'Busy': "#FF9800", 'Mixed': "#FFC107", 'Allocated': "#FF9800",
			'Down': "#F44336", 'Offline': "#9E9E9E", 'Drained': "#795548", 'Drain': "#795548",
			'Deploying': "#03A9F4", 'Destroying': "#9C27B0",
			'Unknown': "#9E9E9E", 'unknown': "#9E9E9E"
		};

		// Strong border colors for professional appearance
		const borderColors = {
			head: "#1976D2",        // Strong blue
			management: "#7B1FA2",  // Strong purple
			switch: "#388E3C",      // Strong green
			subnet: "#2196F3",      // Strong light blue for VCN subnet
			cloud: "#9E9E9E",       // Strong gray for internet cloud
			junction: "#607D8B",    // Strong blue-gray for network junctions
			"core-switch": "#2E7D32", // Strong dark green
			storage: "#F57C00",     // Strong orange
			rack: "#455A64",        // Strong gray
			cabinet: "#37474F",     // Strong dark gray
			compute: "#4CAF50",     // Strong light green (default)
			application: "#2196F3", // Strong blue for application nodes
			media: "#FFC107",       // Strong amber for media nodes (i4i instances)
			"attached-volume": "#9C27B0", // Strong purple for attached volumes
			"attached-volume-deploying": "#FF9800", // Strong orange for deploying volumes
			"placement-group": "#2E7D32", // Strong dark green for placement group (old octagon)
			"placement-group-container": "rgba(139, 195, 74, 0.4)" // Very light green border for placement group container
		};

		const statusColors = {
			healthy: "#4CAF50",
			warning: "#FF9800", 
			critical: "#F44336",
			unknown: "#757575"
		};

		// Draw connections/links with orthogonal routing (industry standard)
		if (data.links && data.links.length > 0) {
			const linkGroup = g.selectAll(".link-group")
				.data(data.links)
				.enter().append("g")
				.attr("class", "link-group");

			linkGroup.each(function(d) {
				const group = d3.select(this);
				const sourceNode = data.nodes.find(n => n.id === d.source);
				const targetNode = data.nodes.find(n => n.id === d.target);
				
				if (sourceNode && targetNode) {
					const x1 = sourceNode.x;
					const y1 = sourceNode.y;
					const x2 = targetNode.x;
					const y2 = targetNode.y;
					
					// Determine link styling based on type
					const isEfaLink = d.type === "efa";
					const isInfiniBandLink = d.type === "infiniband";
					const isSpecialLink = isEfaLink || isInfiniBandLink;
					
					const linkColor = isSpecialLink ? "#2196F3" : "#BDBDBD"; // Blue for EFA/IB, gray for regular
					const linkWidth = isSpecialLink ? 3 : 1; // Thicker line for EFA/IB
					const linkOpacity = isSpecialLink ? 0.8 : 0.6;
					
					if (isSpecialLink) {
						// For EFA/InfiniBand connections, draw a direct line (no orthogonal routing)
						group.append("line")
							.attr("x1", x1)
							.attr("y1", y1)
							.attr("x2", x2)
							.attr("y2", y2)
							.attr("stroke", linkColor)
							.attr("stroke-width", linkWidth)
							.attr("stroke-opacity", linkOpacity)
							.attr("stroke-dasharray", "5,5") // Dashed line for EFA/IB
							.attr("vector-effect", "non-scaling-stroke");
						
						// Add label at the midpoint
						const midX = (x1 + x2) / 2;
						const midY = (y1 + y2) / 2;
						
						group.append("text")
							.attr("x", midX)
							.attr("y", midY - 5)
							.attr("text-anchor", "middle")
							.attr("dy", "0.35em")
							.attr("fill", "#2196F3")
							.attr("font-size", "8px")
							.attr("font-weight", "bold")
							.attr("opacity", 0.7)
							.text(isEfaLink ? "EFA" : "IB");
					} else {
						// Regular network connections with orthogonal routing
						const midX = x1 + (x2 - x1) * 0.5;
						const pathData = `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`;
						
						group.append("path")
							.attr("d", pathData)
							.attr("stroke", linkColor)
							.attr("stroke-width", linkWidth)
							.attr("stroke-opacity", linkOpacity)
							.attr("fill", "none")
							.attr("vector-effect", "non-scaling-stroke");
					}
				}
			});
		}

		// Draw nodes with industry-standard shapes (sort by level to ensure proper rendering order)
		const sortedNodes = [...data.nodes].sort((a, b) => (a.level || 0) - (b.level || 0));
		const nodeGroups = g.selectAll(".node")
			.data(sortedNodes)
			.enter().append("g")
			.attr("class", "node")
			.attr("transform", d => `translate(${d.x},${d.y})`)
			.style("cursor", d => (d.type === "rack" || d.type === "cabinet") ? "pointer" : "default")
			.on("click", (event, d) => {
				if (d.type === "rack" || d.type === "cabinet") {
					setSelectedRack(selectedRack?.id === d.id ? null : d);
				}
			});

		// Industry-standard node shapes
		nodeGroups.each(function(d) {
			const group = d3.select(this);
			
			if (d.type === "head") {
				// Head Node: Rounded rectangle (industry standard)
				group.append("rect")
					.attr("x", -25)
					.attr("y", -20)
					.attr("width", 50)
					.attr("height", 40)
					.attr("rx", 8)
					.attr("ry", 8)
					.attr("fill", nodeColors[d.type])
					.attr("stroke", borderColors[d.type])
					.attr("stroke-width", 2)
					.attr("vector-effect", "non-scaling-stroke");
					
			} else if (d.type === "switch" || d.type === "core-switch") {
				// Switches: Diamond/hexagon shape (industry standard)
				const size = d.type === "core-switch" ? 25 : 20;
				group.append("polygon")
					.attr("points", `0,-${size} ${size},0 0,${size} -${size},0`)
					.attr("fill", nodeColors[d.type])
					.attr("stroke", borderColors[d.type])
					.attr("stroke-width", 2)
					.attr("vector-effect", "non-scaling-stroke");
					
			} else if (d.type === "cloud") {
				// Internet Cloud: Use SVG cloud shape
				const cloudPath = "M25,20 C25,15 20,10 15,10 C10,10 5,15 5,20 C2,20 0,22 0,25 C0,28 2,30 5,30 L25,30 C28,30 30,28 30,25 C30,22 28,20 25,20 Z";
				group.append("path")
					.attr("d", cloudPath)
					.attr("transform", "translate(-24,-30) scale(1.6)")
					.attr("fill", "#E3F2FD")
					.attr("stroke", "#1976D2")
					.attr("stroke-width", 2)
					.attr("vector-effect", "non-scaling-stroke");
					
			} else if (d.type === "storage") {
				// Storage: Cylinder shape (industry standard)
				// Top ellipse (no stroke to avoid crossing lines)
				group.append("ellipse")
					.attr("cx", 0)
					.attr("cy", -15)
					.attr("rx", 20)
					.attr("ry", 6)
					.attr("fill", nodeColors[d.type])
					.attr("stroke", "none");
				
				// Cylinder body (only left and right borders)
				group.append("rect")
					.attr("x", -20)
					.attr("y", -15)
					.attr("width", 40)
					.attr("height", 30)
					.attr("fill", nodeColors[d.type])
					.attr("stroke", "none");
				
				// Left side border
				group.append("line")
					.attr("x1", -20)
					.attr("x2", -20)
					.attr("y1", -15)
					.attr("y2", 15)
					.attr("stroke", borderColors[d.type])
					.attr("stroke-width", 2)
					.attr("vector-effect", "non-scaling-stroke");
				
				// Right side border
				group.append("line")
					.attr("x1", 20)
					.attr("x2", 20)
					.attr("y1", -15)
					.attr("y2", 15)
					.attr("stroke", borderColors[d.type])
					.attr("stroke-width", 2)
					.attr("vector-effect", "non-scaling-stroke");
				
				// Bottom ellipse (no stroke to avoid crossing lines)
				group.append("ellipse")
					.attr("cx", 0)
					.attr("cy", 15)
					.attr("rx", 20)
					.attr("ry", 6)
					.attr("fill", "rgba(245, 124, 0, 0.05)")
					.attr("stroke", "none");
				
				// Top ellipse border (drawn last to be on top)
				group.append("ellipse")
					.attr("cx", 0)
					.attr("cy", -15)
					.attr("rx", 20)
					.attr("ry", 6)
					.attr("fill", "none")
					.attr("stroke", borderColors[d.type])
					.attr("stroke-width", 2)
					.attr("vector-effect", "non-scaling-stroke");
				
				// Bottom ellipse border (drawn last to be on top)
				group.append("ellipse")
					.attr("cx", 0)
					.attr("cy", 15)
					.attr("rx", 20)
					.attr("ry", 6)
					.attr("fill", "none")
					.attr("stroke", borderColors[d.type])
					.attr("stroke-width", 2)
					.attr("vector-effect", "non-scaling-stroke");
					
			} else if (d.type === "junction") {
				// Network Junction: Small circle (industry standard for network nodes)
				group.append("circle")
					.attr("cx", 0)
					.attr("cy", 0)
					.attr("r", 8)
					.attr("fill", nodeColors[d.type])
					.attr("stroke", borderColors[d.type])
					.attr("stroke-width", 2)
					.attr("vector-effect", "non-scaling-stroke");
					
			} else if (d.type === "invisible-junction") {
				// Invisible junction: No visual representation, just connection point
				// Skip rendering - these are logical connection points only
					
			} else if (d.type === "subnet") {
				// VCN Subnet: Rounded rectangle with dotted border (cloud network standard)
				group.append("rect")
					.attr("x", -30)
					.attr("y", -15)
					.attr("width", 60)
					.attr("height", 30)
					.attr("rx", 6)
					.attr("ry", 6)
					.attr("fill", nodeColors[d.type])
					.attr("stroke", borderColors[d.type])
					.attr("stroke-width", 2)
					.attr("stroke-dasharray", "5,5")
					.attr("vector-effect", "non-scaling-stroke");
					
			} else if (d.type === "placement-group-container") {
				// Placement Group Container: Large gray rectangle with rounded corners
				group.append("rect")
					.attr("x", -d.width / 2)
					.attr("y", -d.height / 2)
					.attr("width", d.width)
					.attr("height", d.height)
					.attr("rx", 8)
					.attr("ry", 8)
					.attr("fill", nodeColors["placement-group-container"]) // Very light transparent green
					.attr("stroke", borderColors["placement-group-container"]) // Very light green border
					.attr("stroke-width", 1) // Thinner border for lightweight appearance
					.attr("stroke-dasharray", "8,4") // Dashed border
					.attr("vector-effect", "non-scaling-stroke");
				
				// Add label in top-left corner
				group.append("text")
					.attr("x", -d.width / 2 + 10)
					.attr("y", -d.height / 2 + 15)
					.attr("text-anchor", "start")
					.attr("dy", "0.35em")
					.attr("fill", "rgba(102, 102, 102, 0.8)") // Lighter text color
					.attr("font-size", "10px")
					.attr("font-family", "Arial, sans-serif")
					.attr("font-weight", "500") // Slightly lighter font weight
					.text("Placement Group");
					
			} else if (d.type === "placement-group") {
				// Placement Group: Octagon shape for high-performance networking
				const size = 20;
				const octagonPoints = [];
				for (let i = 0; i < 8; i++) {
					const angle = (i * Math.PI) / 4;
					const x = size * Math.cos(angle);
					const y = size * Math.sin(angle);
					octagonPoints.push(`${x},${y}`);
				}
				
				group.append("polygon")
					.attr("points", octagonPoints.join(" "))
					.attr("fill", "#E8F5E8") // Light green similar to networking components
					.attr("stroke", "#2E7D32") // Dark green border
					.attr("stroke-width", 2)
					.attr("vector-effect", "non-scaling-stroke");
					
			} else if (d.type === "compute") {
				// Compute Nodes: Simple rectangle with state-based colors
				const nodeState = d.state || "unknown";
				const fillColor = stateColors[nodeState] || stateColors["unknown"];
				const strokeColor = stateBorderColors[nodeState] || stateBorderColors["unknown"];
				
				group.append("rect")
					.attr("x", -25)
					.attr("y", -18)
					.attr("width", 50)
					.attr("height", 36)
					.attr("rx", 4)
					.attr("ry", 4)
					.attr("fill", fillColor)
					.attr("stroke", strokeColor)
					.attr("stroke-width", 2)
					.attr("vector-effect", "non-scaling-stroke");
					
			} else if (d.type === "rack") {
				// Rack: Vertical rectangle with horizontal lines (industry standard)
				group.append("rect")
					.attr("x", -15)
					.attr("y", -25)
					.attr("width", 30)
					.attr("height", 50)
					.attr("rx", 4)
					.attr("fill", nodeColors[d.type])
					.attr("stroke", borderColors[d.type])
					.attr("stroke-width", 2)
					.attr("vector-effect", "non-scaling-stroke");
				
				// Rack unit lines
				for (let i = 1; i < 5; i++) {
					group.append("line")
						.attr("x1", -12)
						.attr("x2", 12)
						.attr("y1", -25 + (i * 10))
						.attr("y2", -25 + (i * 10))
						.attr("stroke", borderColors[d.type])
						.attr("stroke-width", 1)
						.attr("stroke-opacity", 0.3)
						.attr("vector-effect", "non-scaling-stroke");
				}
				
				// Node count badge
				group.append("circle")
					.attr("cx", 20)
					.attr("cy", -20)
					.attr("r", 8)
					.attr("fill", borderColors[d.type]);
				
				group.append("text")
					.attr("x", 20)
					.attr("y", -20)
					.attr("text-anchor", "middle")
					.attr("dy", "0.35em")
					.attr("fill", "white")
					.attr("font-size", "7px")
					.attr("font-weight", "bold")
					.text(d.nodeCount);
					
			} else if (d.type === "cabinet") {
				// Cabinet: Large rectangle with grid pattern (industry standard)
				group.append("rect")
					.attr("x", -35)
					.attr("y", -25)
					.attr("width", 70)
					.attr("height", 50)
					.attr("rx", 6)
					.attr("fill", nodeColors[d.type])
					.attr("stroke", borderColors[d.type])
					.attr("stroke-width", 2)
					.attr("vector-effect", "non-scaling-stroke");
				
				// Cabinet grid pattern
				for (let row = 0; row < 3; row++) {
					for (let col = 0; col < 4; col++) {
						group.append("rect")
							.attr("x", -30 + col * 15)
							.attr("y", -20 + row * 13)
							.attr("width", 12)
							.attr("height", 10)
							.attr("rx", 2)
							.attr("fill", row * 4 + col < (d.totalNodes || 0) ? 
								"rgba(76, 175, 80, 0.3)" : "rgba(69, 90, 100, 0.1)")
							.attr("stroke", borderColors[d.type])
							.attr("stroke-width", 1)
							.attr("vector-effect", "non-scaling-stroke");
					}
				}
				
				// Total nodes indicator
				group.append("circle")
					.attr("cx", 30)
					.attr("cy", -20)
					.attr("r", 10)
					.attr("fill", borderColors[d.type]);
				
				group.append("text")
					.attr("x", 30)
					.attr("y", -20)
					.attr("text-anchor", "middle")
					.attr("dy", "0.35em")
					.attr("fill", "white")
					.attr("font-size", "7px")
					.attr("font-weight", "bold")
					.text(d.totalNodes);
				
			} else if (d.type === "application") {
				// Application Nodes: Rectangle with state-based colors and app icon indicator
				const nodeState = d.state || "unknown";
				const fillColor = stateColors[nodeState] || stateColors["unknown"];
				const strokeColor = stateBorderColors[nodeState] || stateBorderColors["unknown"];
				
				group.append("rect")
					.attr("x", -25)
					.attr("y", -18)
					.attr("width", 50)
					.attr("height", 36)
					.attr("rx", 4)
					.attr("ry", 4)
					.attr("fill", fillColor)
					.attr("stroke", strokeColor)
					.attr("stroke-width", 2)
					.attr("vector-effect", "non-scaling-stroke");
				
				// App indicator (small square in corner)
				group.append("rect")
					.attr("x", 15)
					.attr("y", -16)
					.attr("width", 8)
					.attr("height", 8)
					.attr("rx", 1)
					.attr("fill", "#2196F3")
					.attr("stroke", "none");
					
			} else if (d.type === "media") {
				// Media Nodes: Rectangle with state-based colors and media icon indicator
				const nodeState = d.state || "unknown";
				const fillColor = stateColors[nodeState] || stateColors["unknown"];
				const strokeColor = stateBorderColors[nodeState] || stateBorderColors["unknown"];
				
				group.append("rect")
					.attr("x", -25)
					.attr("y", -18)
					.attr("width", 50)
					.attr("height", 36)
					.attr("rx", 4)
					.attr("ry", 4)
					.attr("fill", fillColor)
					.attr("stroke", strokeColor)
					.attr("stroke-width", 2)
					.attr("vector-effect", "non-scaling-stroke");
				
				// Media indicator (disk icon)
				group.append("circle")
					.attr("cx", 18)
					.attr("cy", -13)
					.attr("r", 4)
					.attr("fill", "#FFC107")
					.attr("stroke", "none");
				
				group.append("circle")
					.attr("cx", 18)
					.attr("cy", -13)
					.attr("r", 1.5)
					.attr("fill", "#333")
					.attr("stroke", "none");
					
			} else if (d.type === "attached-volume") {
				// Attached Volume: Small cylinder shape
				// Determine if storage is deploying
				const isStorageDeploying = cluster?.info?.storageDeploying === true;
				const volumeType = isStorageDeploying ? "attached-volume-deploying" : "attached-volume";
				const volumeOpacity = isStorageDeploying ? 0.5 : 1; // More transparent when deploying
				
				// Top ellipse
				group.append("ellipse")
					.attr("cx", 0)
					.attr("cy", -8)
					.attr("rx", 12)
					.attr("ry", 3)
					.attr("fill", nodeColors[volumeType])
					.attr("fill-opacity", volumeOpacity)
					.attr("stroke", "none");
				
				// Cylinder body
				group.append("rect")
					.attr("x", -12)
					.attr("y", -8)
					.attr("width", 24)
					.attr("height", 16)
					.attr("fill", nodeColors[volumeType])
					.attr("fill-opacity", volumeOpacity)
					.attr("stroke", "none");
				
				// Left side border
				group.append("line")
					.attr("x1", -12)
					.attr("x2", -12)
					.attr("y1", -8)
					.attr("y2", 8)
					.attr("stroke", borderColors[volumeType])
					.attr("stroke-opacity", volumeOpacity)
					.attr("stroke-width", 2)
					.attr("vector-effect", "non-scaling-stroke");
				
				// Right side border
				group.append("line")
					.attr("x1", 12)
					.attr("x2", 12)
					.attr("y1", -8)
					.attr("y2", 8)
					.attr("stroke", borderColors[volumeType])
					.attr("stroke-opacity", volumeOpacity)
					.attr("stroke-width", 2)
					.attr("vector-effect", "non-scaling-stroke");
				
				// Bottom ellipse
				group.append("ellipse")
					.attr("cx", 0)
					.attr("cy", 8)
					.attr("rx", 12)
					.attr("ry", 3)
					.attr("fill", nodeColors[volumeType])
					.attr("fill-opacity", volumeOpacity)
					.attr("stroke", "none");
				
				// Top ellipse border
				group.append("ellipse")
					.attr("cx", 0)
					.attr("cy", -8)
					.attr("rx", 12)
					.attr("ry", 3)
					.attr("fill", "none")
					.attr("stroke", borderColors[volumeType])
					.attr("stroke-opacity", volumeOpacity)
					.attr("stroke-width", 2)
					.attr("vector-effect", "non-scaling-stroke");
				
				// Bottom ellipse border
				group.append("ellipse")
					.attr("cx", 0)
					.attr("cy", 8)
					.attr("rx", 12)
					.attr("ry", 3)
					.attr("fill", "none")
					.attr("stroke", borderColors[volumeType])
					.attr("stroke-opacity", volumeOpacity)
					.attr("stroke-width", 2)
					.attr("vector-effect", "non-scaling-stroke");
					
			} else {
				// Default: Circle for other node types (management, etc.)
				const radius = 18;
				group.append("circle")
					.attr("r", radius)
					.attr("fill", nodeColors[d.type] || "rgba(158, 158, 158, 0.1)")
					.attr("stroke", borderColors[d.type] || "#9E9E9E")
					.attr("stroke-width", 2)
					.attr("vector-effect", "non-scaling-stroke");
			}
		});

		// Professional node icons and text (inside shapes with reduced margin)
		nodeGroups.append("text")
			.attr("text-anchor", "middle")
			.attr("dy", d => {
				if (d.type === "head" || d.type === "compute" || d.type === "application" || d.type === "media") {
					return "-0.3em"; // Reduced margin for hostname position
				}
				if (d.type === "storage" || d.type === "attached-volume") return "0.1em"; // Centered with margin
				if (d.type === "rack" || d.type === "cabinet") return "0.1em"; // Centered with margin
				if (d.type === "subnet" || d.type === "placement-group") return "0.1em"; // Centered for subnet and placement group
				if (d.type === "placement-group-container") return ""; // No main text for container (has custom label)
				return "0.1em";
			})
			.attr("fill", d => {
				if (d.type === "head" || d.type === "compute" || d.type === "application" || d.type === "media") {
					return "#37474F"; // Dark text for hostname
				}
				return borderColors[d.type] || "#37474F";
			})
			.attr("font-size", d => {
				if (d.type === "cloud") return "48px"; // Large size for cloud icon
				if (d.type === "management") return "7px"; // Good size for management
				if (d.type === "head") return "7px"; // Slightly larger for head node
				if (d.type === "compute" || d.type === "application" || d.type === "media") return "6px"; // Increased for better readability
				if (d.type === "switch" || d.type === "core-switch") return "7px"; // Smaller for margin
				if (d.type === "subnet" || d.type === "placement-group") return "7px"; // Good size for subnet and placement group
				if (d.type === "storage" || d.type === "attached-volume") return "5px"; // Smaller for volume size
				if (d.type === "placement-group-container") return "0px"; // No main text for container
				return "6px"; // Default smaller for margin
			})
			.attr("font-family", d => {
				if (d.type === "head" || d.type === "compute" || d.type === "management" || d.type === "application" || d.type === "media" || d.type === "storage") {
					return "Arial, sans-serif"; // Regular font for hostname, management, and NFS storage
				}
				return "Material Icons";
			})
			.attr("font-weight", d => {
				if (d.type === "head" || d.type === "compute" || d.type === "application" || d.type === "media" || d.type === "storage") {
					return "600"; // Semi-bold for hostname and NFS storage
				}
				return "normal";
			})
			.attr("opacity", d => {
				// Full opacity for all elements now that positioning provides separation
				return 1;
			})
			.text(d => {
				if (d.type === "head") {
					// Cut long cluster names cleanly
					const clusterName = d.clusterName || d.name || "head-node";
					return clusterName.length > 14 ? clusterName.substring(0, 14) : clusterName;
				}
				if (d.type === "compute" || d.type === "application" || d.type === "media") {
					const hostname = d.hostname || d.name || "compute-node";
					return hostname.length > 12 ? hostname.substring(0, 12) : hostname;
				}
				if (d.type === "attached-volume") {
					// Show volume info if available
					const volumeInfo = d.volumeInfo || d.name || "volume";
					return volumeInfo.length > 8 ? volumeInfo.substring(0, 8) : volumeInfo;
				}
				if (d.type === "subnet") {
					// Show subnet CIDR
					const subnet = d.subnet || "VCN Subnet";
					return subnet.length > 12 ? subnet.substring(0, 12) : subnet;
				}
				if (d.type === "junction") {
					return ""; // No text for junction nodes
				}
				if (d.type === "invisible-junction") {
					return ""; // No text for invisible junction nodes
				}
				switch(d.type) {
					case "management": return "Heidi";                   // Heidi management system
					case "cloud": return "";                            // No text for cloud (using SVG shape)
					case "switch": return "hub";                         // Network hub
					case "core-switch": return "router";                 // Router icon
					case "storage": return "NFS";                       // NFS storage label
					case "rack": return "dns";                          // Server rack
					case "cabinet": return "view_module";               // Grid/cabinet
					case "placement-group": return "Placement Group";   // Placement group label
					case "placement-group-container": return "";        // No text for container (has custom label)
					default: return "device_hub";                       // Generic network device
				}
			});

		// Add IP address for head and compute nodes (with proper margin)
		nodeGroups.append("text")
			.attr("text-anchor", "middle")
			.attr("dy", "1.2em") // Increased margin from hostname
			.attr("fill", "#666")
			.attr("font-size", "5px") // Slightly larger for better readability
			.attr("font-family", "Arial, sans-serif")
			.attr("font-weight", "normal")
			.text(d => {
				if (d.type === "head") {
					// Head node IP from terraform info
					const ip = d.info?.terraform?.publicIp || d.info?.terraform?.privateIp || d.ip || "";
					// Cut long IPs cleanly
					return ip.length > 15 ? ip.substring(0, 15) : ip;
				}
				if (d.type === "compute" || d.type === "application" || d.type === "media") {
					const ip = d.ip || d.address || "";
					return ip.length > 14 ? ip.substring(0, 14) : ip;
				}
				if (d.type === "attached-volume") {
					// Show volume size if available
					const size = d.size || "";
					return size ? `${size}GB` : "";
				}
				return ""; // No IP for other node types
			})
			.style("display", d => {
				// Show IP text for head and compute/application/media nodes that have IP data
				if ((d.type === "head" || d.type === "compute" || d.type === "application" || d.type === "media") && (d.ip || d.address)) {
					return "block";
				}
				// Show size text for attached volumes that have size data
				if (d.type === "attached-volume" && d.size) {
					return "block";
				}
				return "none";
			});

		// Add instance shape for head node (third line)
		nodeGroups.append("text")
			.attr("text-anchor", "middle")
			.attr("dy", "2.4em") // Additional margin from IP
			.attr("fill", "#888")
			.attr("font-size", "4px")
			.attr("font-family", "Arial, sans-serif")
			.attr("font-weight", "normal")
			.text(d => {
				if (d.type === "head" && d.instanceShape) {
					// Show instance shape for head node
					return d.instanceShape.length > 12 ? d.instanceShape.substring(0, 12) : d.instanceShape;
				}
				return "";
			})
			.style("display", d => {
				// Show instance shape only for head nodes that have shape data
				if (d.type === "head" && d.instanceShape) {
					return "block";
				}
				return "none";
			});

		// Add "Internet" text inside cloud nodes
		nodeGroups.append("text")
			.attr("text-anchor", "middle")
			.attr("dy", "1.2em") // Position below the cloud icon
			.attr("fill", "#37474F")
			.attr("font-size", "8px")
			.attr("font-weight", "600")
			.attr("font-family", "Arial, sans-serif")
			.text(d => {
				if (d.type === "cloud") {
					return "Internet";
				}
				return "";
			})
			.style("display", d => {
				return d.type === "cloud" ? "block" : "none";
			});

		// Node type titles under the shapes
		nodeGroups.append("text")
			.attr("text-anchor", "middle")
			.attr("dy", d => {
				if (d.type === "head") return "4.5em";
				if (d.type === "compute" || d.type === "application" || d.type === "media") return "4.2em";
				if (d.type === "attached-volume") return "3.8em";
				if (d.type === "subnet") return "4.5em";
				if (d.type === "cloud") return "4.5em";
				if (d.type === "cabinet") return "5.5em";
				if (d.type === "rack") return "5em";
				if (d.type === "storage") return "4.8em";
				if (d.type === "management") return "4.5em";
				return "4.5em";
			})
			.attr("fill", "#666")
			.attr("font-size", "6px")
			.attr("font-weight", "500")
			.attr("font-family", "Arial, sans-serif")
			.text(d => {
				switch(d.type) {
					case "head": return "Head Node";
					case "compute": return "Compute Node";
					case "application": return "Application Node";
					case "media": return "Media Node";
					case "attached-volume": return "Attached Volume";
					case "subnet": return "VCN Subnet";
					case "management": return "Management";
					case "cloud": return ""; // No title to avoid duplication
					case "storage": return "Storage";
					case "rack": return "Rack";
					case "cabinet": return "Cabinet";
					case "switch": return "Switch";
					case "core-switch": return "Core Switch";
					default: return "";
				}
			});

		// Optional external labels for shape types (very small)
		nodeGroups.append("text")
			.attr("text-anchor", "middle")
			.attr("dy", d => {
				if (d.type === "cabinet") return "4em";
				if (d.type === "rack") return "3.5em";
				if (d.type === "storage") return "3.2em";
				return "3em";
			})
			.attr("fill", "#999")
			.attr("font-size", "6px")
			.attr("font-weight", "400")
			.text(d => {
				// Only show type labels for infrastructure components
				if (d.type === "rack" || d.type === "cabinet" || d.type === "storage") {
					return d.type.toUpperCase();
				}
				return ""; // No external labels for head/compute nodes
			})
			.style("display", d => {
				// Hide external labels when zoomed in (they're not needed with internal text)
				return "none"; // Disabled for cleaner look
			});

		// Add GPU indicator rectangles for nodes with GPUs (corner green rectangle with "GPU" label)
		const gpuIndicators = nodeGroups.append("g")
			.attr("class", "gpu-indicator")
			.style("display", d => d.gpuInfo?.hasGpu ? "block" : "none")
			.style("cursor", "pointer");
		
		// Add GPU rectangle and text only once per indicator
		gpuIndicators.each(function(d) {
			if (d.gpuInfo?.hasGpu) {
				const group = d3.select(this);
				
				// Position in corner of node (top-right corner)
				const cornerX = 18;
				const cornerY = -18;
				
				// Create green rectangle
				group.append("rect")
					.attr("x", cornerX)
					.attr("y", cornerY)
					.attr("width", 24)
					.attr("height", 12)
					.attr("rx", 2)
					.attr("fill", "#4CAF50") // Material Design Green
					.attr("stroke", "#388E3C")
					.attr("stroke-width", 1);
				
				// Add "GPU" text label
				group.append("text")
					.attr("x", cornerX + 12)
					.attr("y", cornerY + 8)
					.attr("text-anchor", "middle")
					.attr("font-family", "Arial, sans-serif")
					.attr("font-size", "8px")
					.attr("font-weight", "bold")
					.attr("fill", "white")
					.text("GPU");
			}
		});
		
		// Add event listeners separately to prevent duplicate handlers
		gpuIndicators
			.on("click", function(event, d) {
				if (d.gpuInfo?.hasGpu) {
					event.stopPropagation(); // Prevent node click events and tooltips
					handlePopoverOpen(event, d.gpuInfo, d);
				}
			})
			.on("mouseover", function(event, d) {
				if (d.gpuInfo?.hasGpu) {
					event.stopPropagation(); // Critical: Prevent node mouseover events
				}
			})
			.on("mouseout", function(event, d) {
				if (d.gpuInfo?.hasGpu) {
					event.stopPropagation(); // Critical: Prevent node mouseout events
				}
			});

		// Add tooltip on hover with delay
		nodeGroups.on("mouseover", function(event, d) {
			// Skip tooltip for placement group containers
			if (d.type === "placement-group-container") {
				return;
			}
			
			// Clear any existing timeouts and tooltips to prevent overlapping  
			if (tooltipTimeout.current) {
				clearTimeout(tooltipTimeout.current);
				tooltipTimeout.current = null;
			}
			d3.selectAll(".topology-tooltip").remove();
			setTooltip(null);
			
			// Don't show tooltip if GPU popover is open
			if (popoverAnchorEl) {
				return;
			}
			
			// Add delay before showing tooltip to prevent overwhelming
			tooltipTimeout.current = setTimeout(() => {
				const tooltip = d3.select("body").append("div")
					.attr("class", "topology-tooltip")
					.style("position", "absolute")
					.style("background", "rgba(255, 255, 255, 0.98)")
					.style("color", "black")
					.style("padding", "12px")
					.style("border-radius", "6px")
					.style("font-size", "12px")
					.style("pointer-events", "none")
					.style("z-index", "1000")
					.style("border", "1px solid #e0e0e0")
					.style("box-shadow", "0 4px 16px rgba(0,0,0,0.12)")
					.style("backdrop-filter", "blur(8px)")
					.style("max-width", "300px")
					.style("opacity", 0);

				// Format the title based on node type
				let titleName = d.name;
				if (d.type === "attached-volume" && d.size && !isNaN(d.name)) {
					// If it's a volume and the name is just a number, add GB
					titleName = `${d.name}GB`;
				}
				
				// Show "NFS" instead of "storage" in tooltip for storage type
				let displayType = d.type === "storage" ? "NFS" : d.type;
				let tooltipContent = `<strong>${titleName}</strong><br/>Type: ${displayType}`;
				
				// Add shared info for NFS storage
				if (d.type === "storage") {
					tooltipContent += `<br/><em>Shared across all compute nodes</em>`;
				}
				
				if (d.type === "compute" || d.type === "application" || d.type === "media") {
					tooltipContent += `<br/>Hostname: ${d.hostname || d.name}`;
					tooltipContent += `<br/>Instance ID: ${d.ip || 'N/A'}`;
					tooltipContent += `<br/>State: ${d.state || 'Unknown'}`;
					if (d.data?.cores) tooltipContent += `<br/>Cores: ${d.data.cores}`;
					if (d.data?.loadAvg) tooltipContent += `<br/>Load: ${d.data.loadAvg}`;
					if (d.type === "application") tooltipContent += `<br/>Type: Application (m5n)`;
					if (d.type === "media") tooltipContent += `<br/>Type: Media (i4i)`;
					
					// Add GPU information if available
					if (d.gpuInfo?.hasGpu) {
						tooltipContent += `<br/><strong>GPU Info:</strong>`;
						tooltipContent += `<br/>• Count: ${d.gpuInfo.count}`;
						tooltipContent += `<br/>• Model: ${d.gpuInfo.model}`;
						if (d.gpuInfo.utilization !== undefined) {
							tooltipContent += `<br/>• Utilization: ${d.gpuInfo.utilization}%`;
						}
						if (d.gpuInfo.temperature !== null && d.gpuInfo.temperature !== undefined) {
							tooltipContent += `<br/>• Temperature: ${d.gpuInfo.temperature}°C`;
						}
					}
				} else if (d.type === "attached-volume") {
					// Check if storage is deploying
					const isStorageDeploying = cluster?.info?.storageDeploying === true;
					
					tooltipContent += `<br/>Volume: ${d.volumeInfo || d.name}`;
					
					// Show "Deploying..." status if storage is deploying
					if (isStorageDeploying) {
						tooltipContent += `<br/><span style="color: #FF9800; font-weight: bold;">⚡ Deploying storage...</span>`;
					}
					
					// Show size if it's a number, otherwise show status
					if (d.size && !isNaN(d.size)) {
						tooltipContent += `<br/>Size: ${d.size}GB`;
					} else if (d.status === 'pending') {
						tooltipContent += `<br/>Status: Pending`;
					}
					// Show IOPS if available (IBM Heidi AI storage)
					if (d.iops && !isNaN(d.iops)) {
						tooltipContent += `<br/>IOPS: ${d.iops.toLocaleString()}`;
					}
					// Show status if available and not pending
					if (d.status && d.status !== 'pending' && !isStorageDeploying) {
						tooltipContent += `<br/>Status: ${d.status.charAt(0).toUpperCase() + d.status.slice(1)}`;
					}
					if (d.attachedTo) tooltipContent += `<br/>Attached to: ${d.attachedTo}`;
					if (d.mountPath) tooltipContent += `<br/>Mount Path: ${d.mountPath}`;
					
					// Add performance data if available and not deploying
					if (d.performanceData && !isStorageDeploying) {
						const perf = d.performanceData;
						tooltipContent += `<hr style="margin: 4px 0 0 0; border: none; border-top: 1px solid #ccc;"/>`;
						if (perf.readIops && perf.writeIops) {
							tooltipContent += `<br/>IOPS: R${perf.readIops.toLocaleString()} / W${perf.writeIops.toLocaleString()}`;
						}
						if (perf.readBandwidth && perf.writeBandwidth) {
							tooltipContent += `<br/>Bandwidth: R${perf.readBandwidth}MB/s / W${perf.writeBandwidth}MB/s`;
						}
						if (perf.latencyRead && perf.latencyWrite) {
							tooltipContent += `<br/>Latency: R${perf.latencyRead}μs / W${perf.latencyWrite}μs`;
						}
					}
				} else if (d.type === "head") {
					tooltipContent += `<br/>Cluster: ${d.clusterName}`;
					if (d.ip) tooltipContent += `<br/>Public IP: ${d.ip}`;
					if (d.privateIp) tooltipContent += `<br/>Private IP: ${d.privateIp}`;
					if (d.instanceShape) tooltipContent += `<br/>Instance: ${d.instanceShape}`;
					
					// Add GPU information if available
					if (d.gpuInfo?.hasGpu) {
						tooltipContent += `<br/><strong>GPU Info:</strong>`;
						tooltipContent += `<br/>• Count: ${d.gpuInfo.count}`;
						tooltipContent += `<br/>• Model: ${d.gpuInfo.model}`;
						if (d.gpuInfo.utilization !== undefined) {
							tooltipContent += `<br/>• Utilization: ${d.gpuInfo.utilization}%`;
						}
						if (d.gpuInfo.temperature !== null && d.gpuInfo.temperature !== undefined) {
							tooltipContent += `<br/>• Temperature: ${d.gpuInfo.temperature}°C`;
						}
						if (d.gpuInfo.gpus && d.gpuInfo.gpus.length > 0) {
							tooltipContent += `<br/>• GPUs:`;
							d.gpuInfo.gpus.forEach((gpu, index) => {
								if (index < 3) { // Limit to first 3 GPUs to avoid very long tooltips
									tooltipContent += `<br/>  GPU ${gpu.index}: ${gpu.utilizationPercent || 0}% @ ${gpu.temperatureC || 'N/A'}°C`;
								}
							});
							if (d.gpuInfo.gpus.length > 3) {
								tooltipContent += `<br/>  ... and ${d.gpuInfo.gpus.length - 3} more`;
							}
						}
					}
				} else if (d.type === "junction") {
					tooltipContent += `<br/>Network Distribution Point`;
				} else if (d.type === "subnet") {
					tooltipContent += `<br/>Virtual Cloud Network`;
					if (d.subnet) tooltipContent += `<br/>CIDR: ${d.subnet}`;
				} else if (d.type === "rack") {
					tooltipContent += `<br/>Nodes: ${d.nodeCount}`;
					tooltipContent += `<br/>Healthy: ${d.healthyCount || 0}`;
					tooltipContent += `<br/>Busy: ${d.busyCount || 0}`;
					tooltipContent += `<br/>Down: ${d.downCount || 0}`;
				} else if (d.type === "cabinet") {
					tooltipContent += `<br/>Total Nodes: ${d.totalNodes}<br/>Racks: ${d.racks?.length || 0}<br/>GPUs: ${d.totalGpus}<br/>Avg Load: ${d.avgLoad?.toFixed(1)}%`;
				}
				
				tooltip.html(tooltipContent)
					.style("left", (event.pageX + 10) + "px")
					.style("top", (event.pageY - 28) + "px");
				
				tooltip.transition().duration(200).style("opacity", 1);
			}, 400); // 400ms delay for node tooltips
		})
		.on("mouseout", function() {
			// Clear tooltip timeout if mouse leaves before tooltip shows
			if (tooltipTimeout.current) {
				clearTimeout(tooltipTimeout.current);
				tooltipTimeout.current = null;
			}
			d3.selectAll(".topology-tooltip").remove();
		});
	};

	const toggleViewMode = () => {
		const modes = ['hierarchical', 'grid', 'force'];
		const currentIndex = modes.indexOf(viewMode);
		setViewMode(modes[(currentIndex + 1) % modes.length]);
	};

	const resetZoom = () => {
		const svg = d3.select(svgRef.current);
		const zoom = svgRef.current.__zoom__;
		if (zoom) {
			// Clear stored transform and interaction state when resetting
			currentTransformRef.current = d3.zoomIdentity;
			isUserInteractingRef.current = false;
			lastInteractionTimeRef.current = 0;
			svg.transition().duration(750).call(
				zoom.transform,
				d3.zoomIdentity
			);
		}
	};

	const zoomIn = () => {
		const svg = d3.select(svgRef.current);
		const zoom = svgRef.current.__zoom__;
		if (zoom) {
			svg.transition().duration(300).call(
				zoom.scaleBy, 1.5
			);
		}
	};

	const zoomOut = () => {
		const svg = d3.select(svgRef.current);
		const zoom = svgRef.current.__zoom__;
		if (zoom) {
			svg.transition().duration(300).call(
				zoom.scaleBy, 1 / 1.5
			);
		}
	};

	return (
		<Paper 
			ref={containerRef} 
			className={classes.container}
			style={{ height: dynamicHeight }}
		>
			{/* Header */}
			<Box className={classes.header}>
				<Chip 
					label={
						(currentNodes?.data?.length || 0) === 0 
							? "Head Node Only" 
							: (() => {
							const count = currentNodes?.data?.length || currentNodes?.length || 0;
							return `${count} compute ${count === 1 ? 'node' : 'nodes'}`;
						})()
					} 
					size="small" 
					color="primary"
                    style={{ 
                        backgroundColor: '#1976d2', 
                        color: 'white',
                        fontWeight: 'bold',
                        borderRadius: '4px',        
                        height: '22px',             
                        paddingLeft: '10px',      
                        paddingRight: '10px'
                    }}
				/>
				<Chip 
					label={`Zoom: ${(zoomLevel * 100).toFixed(0)}%`} 
					size="small" 
					variant="outlined"
					style={{ 
						borderColor: '#666', 
						color: '#333',
						fontWeight: 'bold',
						backgroundColor: 'rgba(255,255,255,0.9)',
                        borderRadius: '4px',
                        height: '22px',
                        paddingLeft: '10px',
                        paddingRight: '10px'

					}}
				/>
			</Box>

			{/* Node State Legend */}
			<div style={{ 
				position: 'absolute',
				bottom: '20px', // Position at bottom
				left: '50%', // Center horizontally
				transform: 'translateX(-50%)', // Center alignment
				padding: '8px 12px', 
				fontSize: '11px', 
				display: 'flex', 
				flexWrap: 'wrap', 
				gap: '12px',
				backgroundColor: 'rgba(255, 255, 255, 0.98)',
				border: '1px solid #e0e0e0',
				borderRadius: '4px',
				boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
				zIndex: 200,
				maxWidth: '600px'
			}}>
				<span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#333' }}>
					<div style={{ width: '14px', height: '10px', backgroundColor: '#4CAF50', border: '1px solid #4CAF50', borderRadius: '2px' }}></div>
					<strong>Available</strong>
				</span>
				<span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#333' }}>
					<div style={{ width: '14px', height: '10px', backgroundColor: '#FF9800', border: '1px solid #FF9800', borderRadius: '2px' }}></div>
					<strong>Busy</strong>
				</span>
				<span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#333' }}>
					<div style={{ width: '14px', height: '10px', backgroundColor: '#F44336', border: '1px solid #F44336', borderRadius: '2px' }}></div>
					<strong>Down</strong>
				</span>
				<span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#333' }}>
					<div style={{ width: '14px', height: '10px', backgroundColor: '#9E9E9E', border: '1px solid #9E9E9E', borderRadius: '2px' }}></div>
					<strong>Offline</strong>
				</span>
				<span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#333' }}>
					<div style={{ width: '14px', height: '10px', backgroundColor: '#03A9F4', border: '1px solid #03A9F4', borderRadius: '2px' }}></div>
					<strong>Deploying</strong>
				</span>
			</div>

			{/* Controls */}
			<div className={classes.controls}>
				<Tooltip title="Toggle View Mode">
					<IconButton size="small" className={classes.controlButton} onClick={toggleViewMode}>
						<ViewModule />
					</IconButton>
				</Tooltip>
				<Tooltip title="Zoom In">
					<IconButton size="small" className={classes.controlButton} onClick={zoomIn}>
						<ZoomIn />
					</IconButton>
				</Tooltip>
				<Tooltip title="Zoom Out">
					<IconButton size="small" className={classes.controlButton} onClick={zoomOut}>
						<ZoomOut />
					</IconButton>
				</Tooltip>
				<Tooltip title="Reset View">
					<IconButton size="small" className={classes.controlButton} onClick={resetZoom}>
						<CenterFocusStrong />
					</IconButton>
				</Tooltip>
				<Tooltip title="Refresh">
					<IconButton size="small" className={classes.controlButton} onClick={createScalableTopology}>
						<Refresh />
					</IconButton>
				</Tooltip>
			</div>

			{/* Main SVG */}
			<svg ref={svgRef} width="100%" height="100%" />

			{/* Legend */}
			<div className={classes.legend}>
				<Typography variant="subtitle2" style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: 8 }}>
					Legend
				</Typography>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
						<div style={{ width: 12, height: 12, backgroundColor: '#1976D2', borderRadius: '50%' }}></div>
						<span>Head Node</span>
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
						<div style={{ width: 12, height: 12, backgroundColor: '#455A64', borderRadius: 2 }}></div>
						<span>{currentNodes?.data?.length > 100 ? 'Cabinet/Rack' : 'Compute Rack'}</span>
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
						<div style={{ width: 12, height: 12, backgroundColor: '#2196F3', borderRadius: '50%' }}></div>
						<span>VCN Subnet</span>
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
						<div style={{ width: 12, height: 12, backgroundColor: '#F57C00', borderRadius: '50%' }}></div>
						<span>Storage</span>
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
						<div style={{ width: 16, height: 10, backgroundColor: 'rgba(158, 158, 158, 0.1)', border: '1px dashed #9E9E9E', borderRadius: '2px' }}></div>
						<span>Placement Group</span>
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
						<div style={{ width: 8, height: 8, backgroundColor: '#2196F3', borderRadius: '50%', color: 'white', fontSize: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>E</div>
						<span>EFA Enabled</span>
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
						<div style={{ width: 8, height: 8, backgroundColor: '#2196F3', borderRadius: '50%', color: 'white', fontSize: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>I</div>
						<span>InfiniBand Enabled</span>
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
						<div style={{ width: 12, height: 2, backgroundColor: '#2196F3', borderStyle: 'dashed', borderWidth: '1px 0', borderColor: '#2196F3' }}></div>
						<span>EFA/IB Connection</span>
					</div>
				</div>
			</div>

			{/* Cluster Stats */}
			<div className={classes.stats}>
				<Typography variant="subtitle2" style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: 8 }}>
					Cluster Statistics
				</Typography>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
					<div>Head Node: <strong style={{ color: '#1976D2' }}>Active</strong></div>
					<div>Compute Nodes: <strong>{currentNodes?.data?.length || 0}</strong></div>
					<div>GPU Nodes: <strong>{currentNodes?.data?.filter(n => getGpuInfoFromMetrics(n.name || n.hostname)?.hasGpu).length || 0}</strong></div>
					<div>Infrastructure: <strong style={{ color: '#4CAF50' }}>Operational</strong></div>
				</div>
			</div>

			{/* Rack Details Panel */}
			{selectedRack && (
				<div className={classes.rackDetails}>
					<Typography variant="subtitle2" style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: 8 }}>
						{selectedRack.name} Details
					</Typography>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
						<div>Type: <strong>{selectedRack.type}</strong></div>
						<div>Status: <strong style={{ color: selectedRack.status === 'healthy' ? '#4CAF50' : '#FF9800' }}>
							{selectedRack.status}
						</strong></div>
						{selectedRack.nodeCount && <div>Nodes: <strong>{selectedRack.nodeCount}</strong></div>}
						{selectedRack.totalNodes && <div>Total Nodes: <strong>{selectedRack.totalNodes}</strong></div>}
						{selectedRack.gpuCount !== undefined && <div>GPU Nodes: <strong>{selectedRack.gpuCount}</strong></div>}
						{selectedRack.totalGpus !== undefined && <div>Total GPUs: <strong>{selectedRack.totalGpus}</strong></div>}
						{selectedRack.avgLoad && <div>Avg Load: <strong>{selectedRack.avgLoad.toFixed(1)}%</strong></div>}
						{selectedRack.racks && (
							<div>
								<div style={{ marginTop: 8, marginBottom: 4 }}>Racks:</div>
								{selectedRack.racks.map(rack => (
									<div key={rack.id} style={{ fontSize: '10px', paddingLeft: 8 }}>
										• {rack.name}: {rack.nodeCount} nodes ({rack.gpuCount} GPU)
									</div>
								))}
							</div>
						)}
					</div>
					<IconButton 
						size="small" 
						style={{ position: 'absolute', top: 4, right: 4 }}
						onClick={() => setSelectedRack(null)}
					>
						×
					</IconButton>
				</div>
			)}
			
			{/* GPU Information Popover */}
			<Popover
				open={Boolean(popoverAnchorEl && selectedGpuData)}
				anchorEl={popoverAnchorEl}
				onClose={handlePopoverClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'center',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'center',
				}}
				PaperProps={{
					style: {
						padding: 16,
						minWidth: 280,
						maxWidth: 400
					}
				}}
			>
				{selectedGpuData && (
					<Box>
						{/* Header with title and close button */}
						<Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
							<Typography variant="h6" style={{ color: nvidiaColors.emerald }}>
								{selectedGpuData.model || 'GPU Information'}
							</Typography>
							<IconButton 
								size="small" 
								onClick={handlePopoverClose}
								style={{ 
									padding: 2,
									margin: '-4px -4px 0 0',
									color: '#999',
									fontSize: '16px'
								}}
							>
								×
							</IconButton>
						</Box>
						
						<Grid container spacing={1}>
							<Grid item xs={6}>
								<Typography variant="body2" color="textSecondary">
									Count:
								</Typography>
								<Typography variant="body1" fontWeight={600}>
									{selectedGpuData.count} GPU{selectedGpuData.count > 1 ? 's' : ''}
								</Typography>
							</Grid>
							
							{selectedGpuData.totalMemoryMB > 0 && (
								<Grid item xs={6}>
									<Typography variant="body2" color="textSecondary">
										Memory per GPU:
									</Typography>
									<Typography variant="body1" fontWeight={600}>
										{(selectedGpuData.totalMemoryMB / selectedGpuData.count / 1024).toFixed(1)} GB
									</Typography>
								</Grid>
							)}
							
							{selectedGpuData.utilization !== undefined && (
								<Grid item xs={6}>
									<Typography variant="body2" color="textSecondary">
										Avg Utilization:
									</Typography>
									<Typography variant="body1" fontWeight={600} style={{ color: nvidiaColors.keppel }}>
										{selectedGpuData.utilization.toFixed(1)}%
									</Typography>
								</Grid>
							)}
							
							{selectedGpuData.temperature !== null && selectedGpuData.temperature !== undefined && (
								<Grid item xs={6}>
									<Typography variant="body2" color="textSecondary">
										Temperature:
									</Typography>
									<Typography variant="body1" fontWeight={600} style={{ color: nvidiaColors.indigoDye }}>
										{selectedGpuData.temperature.toFixed(0)}°C
									</Typography>
								</Grid>
							)}
						</Grid>

						{selectedGpuData.usedMemoryMB > 0 && (
							<Box mt={2}>
								<Typography variant="body2" color="textSecondary" gutterBottom>
									Memory Usage:
								</Typography>
								<Typography variant="body1" fontWeight={600}>
									{(selectedGpuData.usedMemoryMB / 1024).toFixed(1)} GB / {(selectedGpuData.totalMemoryMB / 1024).toFixed(1)} GB
									<span style={{ color: nvidiaColors.verdigris, marginLeft: '8px' }}>
										({((selectedGpuData.usedMemoryMB / selectedGpuData.totalMemoryMB) * 100).toFixed(1)}%)
									</span>
								</Typography>
							</Box>
						)}

						{selectedGpuData.gpus && selectedGpuData.gpus.length > 1 && (
							<Box mt={2}>
								<Typography variant="body2" color="textSecondary" gutterBottom>
									Individual GPUs:
								</Typography>
								<Box display="flex" gap={1} flexWrap="wrap">
									{selectedGpuData.gpus.slice(0, 4).map((gpu, index) => (
										<Chip
											key={index}
											label={`GPU ${gpu.index || index}: ${gpu.utilizationPercent || 0}%`}
											size="small"
											style={{
												backgroundColor: nvidiaColors.verdigris + '20',
												color: nvidiaColors.verdigris
											}}
										/>
									))}
									{selectedGpuData.gpus.length > 4 && (
										<Typography variant="caption" color="textSecondary">
											+{selectedGpuData.gpus.length - 4} more
										</Typography>
									)}
								</Box>
							</Box>
						)}

						{/* Link to GPU Metrics */}
						<Box mt={2} pt={2} style={{ borderTop: '1px solid #e0e0e0' }}>
							<Box
								component="span"
								onClick={() => {
									handlePopoverClose();
									// Navigate to observability tab with GPU metrics focus
									const hostname = selectedNodeData?.name || selectedNodeData?.id;
									const targetHash = hostname ? 
										`observability?selectedNode=${encodeURIComponent(hostname)}` : 
										'observability';
									
									// Set the hash and force hash change event
									window.location.hash = targetHash;
									window.dispatchEvent(new HashChangeEvent('hashchange'));
									
									// Scroll to GPU metrics section after brief delay
									setTimeout(() => {
										const gpuSection = document.querySelector('[data-gpu-metrics]');
										if (gpuSection) {
											gpuSection.scrollIntoView({ behavior: 'smooth' });
										}
									}, 200);
								}}
								style={{
									color: nvidiaColors.emerald,
									cursor: 'pointer',
									textDecoration: 'underline',
									fontSize: '14px',
									display: 'flex',
									alignItems: 'center'
								}}
							>
								<Icon style={{ fontSize: '16px', marginRight: '4px' }}>timeline</Icon>
								View Detailed GPU Metrics
							</Box>
						</Box>
					</Box>
				)}
			</Popover>
		</Paper>
	);
};

export default TopologyMapScalable;
